// LeadEconomics™ Student Assessment Question Bank V2
// 200+ questions for career discovery and self-understanding
// Randomized 85 questions per assessment
// Designed for ages 15-25, globally neutral, culturally inclusive

export const STUDENT_QUESTIONS_V2 = [
  // ═══════════════════════════════════════════════════════════════════════════════
  // PHASE 1: LEARNING & COGNITIVE STYLE (35 questions)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  // Forced Choice Questions
  {
    id: 'stu_learn_1',
    phase: 'learning_cognitive',
    type: 'forced_choice',
    question: 'When learning something completely new, which approach works best for you?',
    subtitle: 'Choose the method that feels most natural',
    options: [
      { id: 'a', text: 'Read about it thoroughly, understand the concepts, then try it', weight: { theoretical: 2, systematic: 1, reading: 1 } },
      { id: 'b', text: 'Jump in and learn by doing, figuring things out along the way', weight: { experiential: 2, hands_on: 1, action_learning: 1 } },
      { id: 'c', text: 'Watch someone demonstrate it first, then practice', weight: { visual: 2, observational: 1, modeling: 1 } },
      { id: 'd', text: 'Discuss it with others and ask questions to understand', weight: { verbal: 2, social_learning: 1, collaborative: 1 } }
    ]
  },
  {
    id: 'stu_learn_2',
    phase: 'learning_cognitive',
    type: 'forced_choice',
    question: 'When you need to remember important information, you typically:',
    subtitle: 'Select your primary memory strategy',
    options: [
      { id: 'a', text: 'Write detailed notes and summaries', weight: { written: 2, organized: 1, verbal: 1 } },
      { id: 'b', text: 'Create visual diagrams, mind maps, or charts', weight: { visual: 2, spatial: 1, conceptual: 1 } },
      { id: 'c', text: 'Repeat it aloud or discuss with someone', weight: { auditory: 2, verbal: 1, social: 1 } },
      { id: 'd', text: 'Connect it to personal experiences or examples', weight: { experiential: 2, associative: 1, personal: 1 } }
    ]
  },
  {
    id: 'stu_learn_3',
    phase: 'learning_cognitive',
    type: 'forced_choice',
    question: 'When given a complex assignment, your first step is usually to:',
    subtitle: 'Choose your natural starting point',
    options: [
      { id: 'a', text: 'Create a detailed plan with timeline and steps', weight: { planning: 2, organized: 1, methodical: 1 } },
      { id: 'b', text: 'Research and gather information extensively', weight: { research: 2, thorough: 1, analytical: 1 } },
      { id: 'c', text: 'Start with the most interesting or creative part', weight: { creative: 1, interest_driven: 1, spontaneous: 1 } },
      { id: 'd', text: 'Discuss requirements with others to clarify understanding', weight: { collaborative: 2, clarifying: 1, social: 1 } }
    ]
  },
  
  // Situational Questions
  {
    id: 'stu_learn_4',
    phase: 'learning_cognitive',
    type: 'scenario',
    question: 'You are struggling to understand a difficult concept that will be on an upcoming exam.',
    subtitle: 'What is your approach?',
    options: [
      { id: 'a', text: 'Re-read the material multiple times and take more detailed notes', weight: { persistent: 1, independent: 1, reading: 1 } },
      { id: 'b', text: 'Find different explanations online, videos, or alternative resources', weight: { resourceful: 1, multimodal: 1, proactive: 1 } },
      { id: 'c', text: 'Ask a teacher, tutor, or knowledgeable friend to explain', weight: { help_seeking: 1, collaborative: 1, social: 1 } },
      { id: 'd', text: 'Try to apply the concept through practice problems', weight: { application: 1, experiential: 1, active: 1 } }
    ]
  },
  {
    id: 'stu_learn_5',
    phase: 'learning_cognitive',
    type: 'scenario',
    question: 'You have three major assignments due around the same time.',
    subtitle: 'How do you manage this situation?',
    options: [
      { id: 'a', text: 'Create a schedule dividing time across all three equally', weight: { organized: 1, balanced: 1, planning: 1 } },
      { id: 'b', text: 'Complete one fully before moving to the next', weight: { focused: 1, sequential: 1, depth: 1 } },
      { id: 'c', text: 'Start with the easiest to build momentum', weight: { momentum: 1, practical: 1, confidence: 1 } },
      { id: 'd', text: 'Begin with the most important or most difficult', weight: { priority: 1, strategic: 1, challenging: 1 } }
    ]
  },
  {
    id: 'stu_learn_6',
    phase: 'learning_cognitive',
    type: 'scenario',
    question: 'You discover that your approach to a project has been wrong after investing significant effort.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Start over with the correct approach immediately', weight: { adaptable: 1, decisive: 1, quality: 1 } },
      { id: 'b', text: 'See if any of the work can be salvaged or repurposed', weight: { pragmatic: 1, resourceful: 1, efficiency: 1 } },
      { id: 'c', text: 'Feel frustrated and need time to process before continuing', weight: { emotional: 1, processing: 1, reflective: 1 } },
      { id: 'd', text: 'Seek advice on how to best move forward', weight: { collaborative: 1, help_seeking: 1, learning: 1 } }
    ]
  },
  
  // Reflection Questions
  {
    id: 'stu_learn_7',
    phase: 'learning_cognitive',
    type: 'reflection',
    question: 'When solving problems, you generally prefer to:',
    subtitle: 'Choose what feels most natural',
    options: [
      { id: 'a', text: 'Follow established methods and proven approaches', weight: { conventional: 1, systematic: 1, structured: 1 } },
      { id: 'b', text: 'Find creative or unconventional solutions', weight: { creative: 2, innovative: 1, original: 1 } },
      { id: 'c', text: 'Adapt methods based on the specific situation', weight: { adaptable: 1, contextual: 1, flexible: 1 } },
      { id: 'd', text: 'Combine different approaches that seem relevant', weight: { integrative: 1, comprehensive: 1, synthesizing: 1 } }
    ]
  },
  {
    id: 'stu_learn_8',
    phase: 'learning_cognitive',
    type: 'reflection',
    question: 'Your concentration is typically best when:',
    subtitle: 'Select the environment that helps you focus',
    options: [
      { id: 'a', text: 'Working alone in a quiet space', weight: { independent: 2, focused: 1, introverted: 1 } },
      { id: 'b', text: 'Working in a lively environment with background activity', weight: { extroverted: 1, stimulation: 1, ambient: 1 } },
      { id: 'c', text: 'Working alongside others who are also focused', weight: { parallel_work: 1, social_presence: 1, balanced: 1 } },
      { id: 'd', text: 'It depends on what I am working on', weight: { flexible: 1, contextual: 1, adaptable: 1 } }
    ]
  },
  {
    id: 'stu_learn_9',
    phase: 'learning_cognitive',
    type: 'reflection',
    question: 'When you encounter information that challenges something you believed:',
    subtitle: 'Choose your typical response',
    options: [
      { id: 'a', text: 'You investigate to determine which view is correct', weight: { analytical: 2, truth_seeking: 1, open: 1 } },
      { id: 'b', text: 'You tend to maintain your original belief unless evidence is overwhelming', weight: { conviction: 1, stable: 1, resistance: 1 } },
      { id: 'c', text: 'You enjoy the intellectual challenge of reconsidering', weight: { curious: 2, growth: 1, intellectual: 1 } },
      { id: 'd', text: 'You discuss with others to help form your view', weight: { collaborative: 1, social_processing: 1, deliberate: 1 } }
    ]
  },
  {
    id: 'stu_learn_10',
    phase: 'learning_cognitive',
    type: 'reflection',
    question: 'When working on a long-term project, you tend to:',
    subtitle: 'Select the pattern that describes you best',
    options: [
      { id: 'a', text: 'Work steadily throughout, making consistent progress', weight: { disciplined: 2, consistent: 1, steady: 1 } },
      { id: 'b', text: 'Work intensively near deadlines when pressure motivates', weight: { deadline_driven: 1, burst: 1, pressure: 1 } },
      { id: 'c', text: 'Alternate between periods of intense work and breaks', weight: { rhythmic: 1, balanced: 1, recovery: 1 } },
      { id: 'd', text: 'Vary depending on interest level in the project', weight: { interest_driven: 1, variable: 1, engagement: 1 } }
    ]
  },
  
  // More Learning Questions
  {
    id: 'stu_learn_11',
    phase: 'learning_cognitive',
    type: 'forced_choice',
    question: 'In group discussions or debates, you typically:',
    subtitle: 'Choose your natural tendency',
    options: [
      { id: 'a', text: 'Share your views confidently and early', weight: { assertive: 2, confident: 1, expressive: 1 } },
      { id: 'b', text: 'Listen carefully before contributing', weight: { reflective: 1, thoughtful: 1, observant: 1 } },
      { id: 'c', text: 'Ask questions to understand others perspectives', weight: { curious: 1, understanding: 1, questioning: 1 } },
      { id: 'd', text: 'Support others ideas and build on them', weight: { supportive: 1, collaborative: 1, building: 1 } }
    ]
  },
  {
    id: 'stu_learn_12',
    phase: 'learning_cognitive',
    type: 'scenario',
    question: 'You receive feedback on your work that you disagree with.',
    subtitle: 'How do you typically respond?',
    options: [
      { id: 'a', text: 'Consider the feedback carefully—others see things I might miss', weight: { open: 1, humble: 1, growth: 1 } },
      { id: 'b', text: 'Respectfully explain your perspective and reasoning', weight: { assertive: 1, confident: 1, communicative: 1 } },
      { id: 'c', text: 'Seek clarification to understand their viewpoint better', weight: { curious: 1, understanding: 1, learning: 1 } },
      { id: 'd', text: 'Make changes to satisfy the feedback giver', weight: { accommodating: 1, compliant: 1, pragmatic: 1 } }
    ]
  },
  {
    id: 'stu_learn_13',
    phase: 'learning_cognitive',
    type: 'reflection',
    question: 'Your natural thinking style tends to be more:',
    subtitle: 'Choose the description that fits you better',
    options: [
      { id: 'a', text: 'Detailed and specific—I notice the small things', weight: { detail: 2, precise: 1, focused: 1 } },
      { id: 'b', text: 'Big picture—I see overall patterns and themes', weight: { conceptual: 2, holistic: 1, strategic: 1 } },
      { id: 'c', text: 'Balanced—I move between detail and big picture', weight: { versatile: 1, balanced: 1, adaptive: 1 } },
      { id: 'd', text: 'It depends on what the situation requires', weight: { contextual: 1, responsive: 1, flexible: 1 } }
    ]
  },
  {
    id: 'stu_learn_14',
    phase: 'learning_cognitive',
    type: 'forced_choice',
    question: 'When faced with a question you cannot immediately answer:',
    subtitle: 'Select your natural response',
    options: [
      { id: 'a', text: 'Think it through logically to work toward an answer', weight: { analytical: 2, logical: 1, reasoning: 1 } },
      { id: 'b', text: 'Trust your gut feeling or intuition', weight: { intuitive: 2, instinctive: 1, feeling: 1 } },
      { id: 'c', text: 'Research or look up the answer', weight: { research: 1, thorough: 1, accurate: 1 } },
      { id: 'd', text: 'Ask someone who might know', weight: { social: 1, collaborative: 1, resourceful: 1 } }
    ]
  },
  {
    id: 'stu_learn_15',
    phase: 'learning_cognitive',
    type: 'scenario',
    question: 'You are asked to present on a topic you find boring or uninteresting.',
    subtitle: 'How do you approach this?',
    options: [
      { id: 'a', text: 'Find an angle that makes it interesting to you', weight: { creative: 1, adaptable: 1, reframing: 1 } },
      { id: 'b', text: 'Do your best despite low motivation—responsibility matters', weight: { disciplined: 1, responsible: 1, dutiful: 1 } },
      { id: 'c', text: 'Focus on delivering value to your audience', weight: { service: 1, other_focused: 1, professional: 1 } },
      { id: 'd', text: 'Get it done quickly with minimum required effort', weight: { efficient: 1, pragmatic: 1, selective: 1 } }
    ]
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // PHASE 2: INTERESTS & CAREER ORIENTATION (35 questions)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  {
    id: 'stu_career_1',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'Which type of work activity appeals to you most?',
    subtitle: 'Choose the option that excites you most',
    options: [
      { id: 'a', text: 'Creating new products, designs, content, or artistic works', weight: { creating: 2, artistic: 1, design: 1 } },
      { id: 'b', text: 'Analyzing data, solving problems, conducting research', weight: { analytical: 2, investigative: 1, research: 1 } },
      { id: 'c', text: 'Working with people—teaching, helping, healing, guiding', weight: { social: 2, helping: 1, service: 1 } },
      { id: 'd', text: 'Building, organizing, or managing operations and systems', weight: { enterprising: 1, organizing: 1, building: 1 } }
    ]
  },
  {
    id: 'stu_career_2',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'In your ideal career, what would you value most?',
    subtitle: 'Select your top priority',
    options: [
      { id: 'a', text: 'Financial security and stability', weight: { security: 2, stability: 1, practical: 1 } },
      { id: 'b', text: 'Creative freedom and self-expression', weight: { creative: 2, autonomy: 1, expression: 1 } },
      { id: 'c', text: 'Making a positive impact on others or society', weight: { impact: 2, purpose: 1, service: 1 } },
      { id: 'd', text: 'Achievement, recognition, and advancement', weight: { achievement: 2, recognition: 1, ambition: 1 } }
    ]
  },
  {
    id: 'stu_career_3',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'Which environment would you prefer to work in?',
    subtitle: 'Choose your preferred setting',
    options: [
      { id: 'a', text: 'Office with structured hours and clear responsibilities', weight: { structure: 2, stability: 1, conventional: 1 } },
      { id: 'b', text: 'Flexible environment where I manage my own schedule', weight: { autonomy: 2, flexibility: 1, independent: 1 } },
      { id: 'c', text: 'Dynamic environment with variety and change', weight: { variety: 2, dynamic: 1, stimulation: 1 } },
      { id: 'd', text: 'Collaborative team environment', weight: { social: 2, teamwork: 1, collaborative: 1 } }
    ]
  },
  {
    id: 'stu_career_4',
    phase: 'career_orientation',
    type: 'scenario',
    question: 'You have the opportunity to choose between two job offers after graduation.',
    subtitle: 'Which would you prefer?',
    options: [
      { id: 'a', text: 'Lower salary but work you find meaningful and aligned with your values', weight: { values: 2, meaning: 1, purpose: 1 } },
      { id: 'b', text: 'Higher salary in a field you find less interesting', weight: { financial: 2, practical: 1, security: 1 } },
      { id: 'c', text: 'Moderate salary with high growth potential', weight: { growth: 2, potential: 1, advancement: 1 } },
      { id: 'd', text: 'Would not choose without more information about both', weight: { analytical: 1, cautious: 1, thorough: 1 } }
    ]
  },
  {
    id: 'stu_career_5',
    phase: 'career_orientation',
    type: 'reflection',
    question: 'How important is it that your career aligns with your personal interests?',
    subtitle: 'Choose what reflects your view',
    options: [
      { id: 'a', text: 'Essential—I need to love what I do', weight: { passion: 2, alignment: 1, fulfillment: 1 } },
      { id: 'b', text: 'Important but practical considerations also matter', weight: { balanced: 1, realistic: 1, pragmatic: 1 } },
      { id: 'c', text: 'Work is work—interests can be pursued outside', weight: { practical: 2, separation: 1, realistic: 1 } },
      { id: 'd', text: 'I can learn to enjoy most work if it pays well', weight: { adaptable: 1, financial: 1, flexible: 1 } }
    ]
  },
  {
    id: 'stu_career_6',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'Which of these activities sounds most appealing to you?',
    subtitle: 'Select your preference',
    options: [
      { id: 'a', text: 'Designing and building something new', weight: { creating: 2, technical: 1, building: 1 } },
      { id: 'b', text: 'Persuading and negotiating with others', weight: { persuading: 2, enterprising: 1, influential: 1 } },
      { id: 'c', text: 'Researching and discovering new knowledge', weight: { research: 2, investigative: 1, discovery: 1 } },
      { id: 'd', text: 'Organizing and improving processes', weight: { organizing: 2, systematic: 1, improving: 1 } }
    ]
  },
  {
    id: 'stu_career_7',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'What motivates you most to work hard?',
    subtitle: 'Choose your primary motivator',
    options: [
      { id: 'a', text: 'Personal achievement and excellence', weight: { achievement: 2, excellence: 1, mastery: 1 } },
      { id: 'b', text: 'Recognition and appreciation from others', weight: { recognition: 2, external: 1, appreciation: 1 } },
      { id: 'c', text: 'Contributing to something meaningful', weight: { purpose: 2, meaning: 1, contribution: 1 } },
      { id: 'd', text: 'Financial rewards and advancement', weight: { financial: 2, advancement: 1, rewards: 1 } }
    ]
  },
  {
    id: 'stu_career_8',
    phase: 'career_orientation',
    type: 'scenario',
    question: 'You have the chance to start your own business or join an established company.',
    subtitle: 'Which appeals more to you?',
    options: [
      { id: 'a', text: 'Start my own business—I want to build something', weight: { entrepreneurial: 2, independence: 1, building: 1 } },
      { id: 'b', text: 'Join established company—learn from others first', weight: { learning: 1, stability: 1, mentorship: 1 } },
      { id: 'c', text: 'Join a startup—entrepreneurial without full risk', weight: { balanced: 1, innovative: 1, growth: 1 } },
      { id: 'd', text: 'Depends on the specific opportunity', weight: { pragmatic: 1, analytical: 1, contextual: 1 } }
    ]
  },
  {
    id: 'stu_career_9',
    phase: 'career_orientation',
    type: 'reflection',
    question: 'How do you feel about competition in your career or studies?',
    subtitle: 'Select your orientation toward competition',
    options: [
      { id: 'a', text: 'I thrive on competition—it brings out my best', weight: { competitive: 2, driven: 1, motivated: 1 } },
      { id: 'b', text: 'I prefer collaboration over competition', weight: { collaborative: 2, cooperative: 1, team: 1 } },
      { id: 'c', text: 'I compete with myself—personal improvement matters most', weight: { self_competitive: 1, growth: 1, mastery: 1 } },
      { id: 'd', text: 'I adapt to whatever the environment requires', weight: { adaptive: 1, flexible: 1, contextual: 1 } }
    ]
  },
  {
    id: 'stu_career_10',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'Which role would you naturally take in a group project?',
    subtitle: 'Choose your most natural role',
    options: [
      { id: 'a', text: 'The leader who coordinates and makes decisions', weight: { leadership: 2, coordinating: 1, decisive: 1 } },
      { id: 'b', text: 'The creative who generates ideas and solutions', weight: { creative: 2, ideation: 1, innovative: 1 } },
      { id: 'c', text: 'The analyst who researches and ensures accuracy', weight: { analytical: 2, research: 1, detail: 1 } },
      { id: 'd', text: 'The implementer who gets things done', weight: { implementing: 2, execution: 1, practical: 1 } }
    ]
  },
  {
    id: 'stu_career_11',
    phase: 'career_orientation',
    type: 'scenario',
    question: 'You discover a career path that interests you but is very competitive with uncertain outcomes.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Pursue it fully—passion and effort will prevail', weight: { optimistic: 1, passionate: 1, committed: 1 } },
      { id: 'b', text: 'Pursue it while developing a backup plan', weight: { strategic: 1, practical: 1, prepared: 1 } },
      { id: 'c', text: 'Research success rates and requirements carefully first', weight: { analytical: 1, cautious: 1, informed: 1 } },
      { id: 'd', text: 'Consider more stable alternatives with similar appeal', weight: { pragmatic: 1, security: 1, realistic: 1 } }
    ]
  },
  {
    id: 'stu_career_12',
    phase: 'career_orientation',
    type: 'reflection',
    question: 'How important is work-life balance in your career plans?',
    subtitle: 'Choose your priority level',
    options: [
      { id: 'a', text: 'Very important—I want time for life outside work', weight: { balance: 2, boundaries: 1, wellbeing: 1 } },
      { id: 'b', text: 'Important but I can sacrifice early in career for later rewards', weight: { strategic: 1, investment: 1, deferred: 1 } },
      { id: 'c', text: 'If I love my work, balance is less critical', weight: { passion: 1, integration: 1, engaged: 1 } },
      { id: 'd', text: 'Depends on life stage and circumstances', weight: { flexible: 1, contextual: 1, adaptive: 1 } }
    ]
  },
  {
    id: 'stu_career_13',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'Which statement best describes your career ambition?',
    subtitle: 'Select what resonates most',
    options: [
      { id: 'a', text: 'I want to reach the top of my field or organization', weight: { ambitious: 2, achievement: 1, driven: 1 } },
      { id: 'b', text: 'I want to be excellent at what I do and respected for it', weight: { mastery: 2, expertise: 1, respect: 1 } },
      { id: 'c', text: 'I want work that is meaningful and contributes positively', weight: { purpose: 2, meaning: 1, values: 1 } },
      { id: 'd', text: 'I want security and a comfortable life', weight: { security: 2, comfort: 1, stability: 1 } }
    ]
  },
  {
    id: 'stu_career_14',
    phase: 'career_orientation',
    type: 'scenario',
    question: 'Your dream job requires relocating to another city or country.',
    subtitle: 'How do you feel about this?',
    options: [
      { id: 'a', text: 'Excited—new experiences and opportunities', weight: { adventurous: 2, open: 1, growth: 1 } },
      { id: 'b', text: 'Willing if the opportunity is right', weight: { pragmatic: 1, flexible: 1, conditional: 1 } },
      { id: 'c', text: 'Hesitant—relationships and roots matter to me', weight: { rooted: 1, relationships: 1, stability: 1 } },
      { id: 'd', text: 'Would seek similar opportunities locally first', weight: { practical: 1, local: 1, cautious: 1 } }
    ]
  },
  {
    id: 'stu_career_15',
    phase: 'career_orientation',
    type: 'reflection',
    question: 'How clear is your vision of what career you want to pursue?',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Very clear—I know exactly what I want', weight: { focused: 2, certain: 1, directed: 1 } },
      { id: 'b', text: 'General direction is clear, specifics are open', weight: { directional: 1, flexible: 1, exploring: 1 } },
      { id: 'c', text: 'Exploring multiple options, haven\'t decided', weight: { exploring: 2, open: 1, discovering: 1 } },
      { id: 'd', text: 'Uncertain—still figuring out what I want', weight: { uncertain: 1, discovering: 1, open: 1 } }
    ]
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // PHASE 3: PERSONALITY & SOCIAL ORIENTATION (35 questions)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  {
    id: 'stu_social_1',
    phase: 'social_personality',
    type: 'forced_choice',
    question: 'After a busy day with many social interactions, you typically feel:',
    subtitle: 'Choose your natural response',
    options: [
      { id: 'a', text: 'Energized and satisfied', weight: { extroverted: 2, social_energy: 1, people_oriented: 1 } },
      { id: 'b', text: 'Drained and need quiet time to recharge', weight: { introverted: 2, solitary: 1, reflective: 1 } },
      { id: 'c', text: 'Depends on the quality of the interactions', weight: { selective: 1, discerning: 1, quality_focused: 1 } },
      { id: 'd', text: 'A mix—satisfied but ready for some alone time', weight: { ambivert: 1, balanced: 1, moderate: 1 } }
    ]
  },
  {
    id: 'stu_social_2',
    phase: 'social_personality',
    type: 'forced_choice',
    question: 'In social situations, you generally:',
    subtitle: 'Select your typical behavior',
    options: [
      { id: 'a', text: 'Initiate conversations and engage easily with new people', weight: { initiating: 2, confident: 1, outgoing: 1 } },
      { id: 'b', text: 'Wait to be approached or engage in smaller groups', weight: { reserved: 1, cautious: 1, selective: 1 } },
      { id: 'c', text: 'Prefer deep conversations with few people over small talk with many', weight: { depth: 1, quality: 1, intimate: 1 } },
      { id: 'd', text: 'Adapt to the situation—sometimes outgoing, sometimes reserved', weight: { adaptive: 1, flexible: 1, contextual: 1 } }
    ]
  },
  {
    id: 'stu_social_3',
    phase: 'social_personality',
    type: 'scenario',
    question: 'A close friend shares a problem and seems upset.',
    subtitle: 'What is your natural response?',
    options: [
      { id: 'a', text: 'Listen empathetically and offer emotional support', weight: { empathetic: 2, supportive: 1, listening: 1 } },
      { id: 'b', text: 'Try to help solve the problem with practical suggestions', weight: { problem_solving: 1, practical: 1, helpful: 1 } },
      { id: 'c', text: 'Share a similar experience to show understanding', weight: { relating: 1, sharing: 1, connecting: 1 } },
      { id: 'd', text: 'Ask questions to understand the situation better', weight: { curious: 1, understanding: 1, analytical: 1 } }
    ]
  },
  {
    id: 'stu_social_4',
    phase: 'social_personality',
    type: 'reflection',
    question: 'When it comes to rules and expectations, you tend to:',
    subtitle: 'Choose what describes you best',
    options: [
      { id: 'a', text: 'Follow them carefully—structure is important', weight: { rule_following: 2, conventional: 1, structured: 1 } },
      { id: 'b', text: 'Question them if they don\'t make sense', weight: { questioning: 1, independent: 1, critical: 1 } },
      { id: 'c', text: 'Find creative ways to work within or around them', weight: { creative: 1, flexible: 1, adaptive: 1 } },
      { id: 'd', text: 'Follow important ones but ignore minor or pointless ones', weight: { selective: 1, pragmatic: 1, discretion: 1 } }
    ]
  },
  {
    id: 'stu_social_5',
    phase: 'social_personality',
    type: 'scenario',
    question: 'You are part of a team where one member is not contributing fairly.',
    subtitle: 'How do you handle this?',
    options: [
      { id: 'a', text: 'Speak to them directly about the issue', weight: { direct: 2, assertive: 1, confronting: 1 } },
      { id: 'b', text: 'Raise it with the group or leader', weight: { collaborative: 1, procedural: 1, escalating: 1 } },
      { id: 'c', text: 'Work around it—do extra to ensure project success', weight: { accommodating: 1, responsible: 1, avoiding: 1 } },
      { id: 'd', text: 'Try to understand why they are not contributing', weight: { empathetic: 1, understanding: 1, curious: 1 } }
    ]
  },
  {
    id: 'stu_social_6',
    phase: 'social_personality',
    type: 'reflection',
    question: 'How do you typically make important decisions?',
    subtitle: 'Choose your primary approach',
    options: [
      { id: 'a', text: 'Analyze logically and choose the most rational option', weight: { logical: 2, analytical: 1, rational: 1 } },
      { id: 'b', text: 'Consider how decisions affect others involved', weight: { considerate: 2, social: 1, empathetic: 1 } },
      { id: 'c', text: 'Trust my gut feeling and intuition', weight: { intuitive: 2, feeling: 1, instinctive: 1 } },
      { id: 'd', text: 'Seek input from trusted others before deciding', weight: { collaborative: 1, advice_seeking: 1, cautious: 1 } }
    ]
  },
  {
    id: 'stu_social_7',
    phase: 'social_personality',
    type: 'forced_choice',
    question: 'Your reaction to unexpected changes in plans is usually:',
    subtitle: 'Select your typical response',
    options: [
      { id: 'a', text: 'Flexibility is fine—changes can bring new opportunities', weight: { flexible: 2, adaptable: 1, open: 1 } },
      { id: 'b', text: 'Mild frustration but I adapt quickly', weight: { adaptive: 1, resilient: 1, moderate: 1 } },
      { id: 'c', text: 'Prefer knowing plans in advance and sticking to them', weight: { structured: 1, planning: 1, stability: 1 } },
      { id: 'd', text: 'Depends on how significant the change is', weight: { contextual: 1, proportional: 1, reasonable: 1 } }
    ]
  },
  {
    id: 'stu_social_8',
    phase: 'social_personality',
    type: 'scenario',
    question: 'You witness someone being treated unfairly.',
    subtitle: 'What is your most likely response?',
    options: [
      { id: 'a', text: 'Speak up or intervene directly', weight: { courageous: 2, principled: 1, active: 1 } },
      { id: 'b', text: 'Support the person privately afterward', weight: { supportive: 1, cautious: 1, caring: 1 } },
      { id: 'c', text: 'Report it to someone in authority', weight: { procedural: 1, responsible: 1, indirect: 1 } },
      { id: 'd', text: 'Feel uncomfortable but not get involved', weight: { passive: 1, avoidant: 1, conflict_averse: 1 } }
    ]
  },
  {
    id: 'stu_social_9',
    phase: 'social_personality',
    type: 'reflection',
    question: 'When meeting new people, you tend to:',
    subtitle: 'Choose your natural tendency',
    options: [
      { id: 'a', text: 'Make friends easily and enjoy the process', weight: { sociable: 2, friendly: 1, open: 1 } },
      { id: 'b', text: 'Take time to warm up but form strong connections', weight: { slow_warming: 1, deep: 1, selective: 1 } },
      { id: 'c', text: 'Be selective about who you invest in getting to know', weight: { selective: 2, discerning: 1, quality: 1 } },
      { id: 'd', text: 'Prefer meeting people through existing connections', weight: { networked: 1, referral: 1, comfortable: 1 } }
    ]
  },
  {
    id: 'stu_social_10',
    phase: 'social_personality',
    type: 'forced_choice',
    question: 'In terms of expressing your opinions, you typically:',
    subtitle: 'Select what describes you best',
    options: [
      { id: 'a', text: 'Share opinions openly and confidently', weight: { expressive: 2, confident: 1, assertive: 1 } },
      { id: 'b', text: 'Share when asked but don\'t volunteer often', weight: { reserved: 1, modest: 1, selective: 1 } },
      { id: 'c', text: 'Consider your audience before deciding what to share', weight: { strategic: 1, aware: 1, diplomatic: 1 } },
      { id: 'd', text: 'Keep opinions private unless with close friends', weight: { private: 1, guarded: 1, intimate: 1 } }
    ]
  },
  {
    id: 'stu_social_11',
    phase: 'social_personality',
    type: 'scenario',
    question: 'You disagree with a popular opinion held by your friends or peer group.',
    subtitle: 'How do you typically handle this?',
    options: [
      { id: 'a', text: 'Express your different view openly', weight: { independent: 2, authentic: 1, courageous: 1 } },
      { id: 'b', text: 'Keep your opinion to yourself to avoid conflict', weight: { accommodating: 1, harmony: 1, avoidant: 1 } },
      { id: 'c', text: 'Share your view diplomatically without being confrontational', weight: { diplomatic: 1, tactful: 1, balanced: 1 } },
      { id: 'd', text: 'Reconsider your view—maybe you\'re missing something', weight: { open: 1, humble: 1, reflective: 1 } }
    ]
  },
  {
    id: 'stu_social_12',
    phase: 'social_personality',
    type: 'reflection',
    question: 'How do you typically handle criticism or negative feedback?',
    subtitle: 'Choose your natural response',
    options: [
      { id: 'a', text: 'Take it seriously and work to improve', weight: { growth: 2, responsive: 1, improving: 1 } },
      { id: 'b', text: 'Feel hurt initially but eventually process it', weight: { sensitive: 1, processing: 1, resilient: 1 } },
      { id: 'c', text: 'Evaluate if it\'s valid before accepting', weight: { analytical: 1, discerning: 1, selective: 1 } },
      { id: 'd', text: 'Not affected much—confidence comes from within', weight: { confident: 1, self_assured: 1, independent: 1 } }
    ]
  },
  {
    id: 'stu_social_13',
    phase: 'social_personality',
    type: 'forced_choice',
    question: 'Your approach to conflict is typically:',
    subtitle: 'Select your natural tendency',
    options: [
      { id: 'a', text: 'Address it directly to resolve it quickly', weight: { direct: 2, confronting: 1, resolving: 1 } },
      { id: 'b', text: 'Avoid it if possible—conflict is uncomfortable', weight: { avoidant: 1, peaceful: 1, harmonious: 1 } },
      { id: 'c', text: 'Seek to understand both sides and find compromise', weight: { mediating: 1, balanced: 1, diplomatic: 1 } },
      { id: 'd', text: 'Give it time—many conflicts resolve themselves', weight: { patient: 1, passive: 1, time: 1 } }
    ]
  },
  {
    id: 'stu_social_14',
    phase: 'social_personality',
    type: 'scenario',
    question: 'You need to lead a group of people who don\'t know each other well.',
    subtitle: 'What is your first priority?',
    options: [
      { id: 'a', text: 'Set clear goals and structure for the group', weight: { task: 2, organized: 1, directive: 1 } },
      { id: 'b', text: 'Help people get to know each other and build rapport', weight: { relationship: 2, connection: 1, team_building: 1 } },
      { id: 'c', text: 'Understand each person\'s strengths and interests', weight: { individual: 1, strengths: 1, understanding: 1 } },
      { id: 'd', text: 'Get started on the work—relationships will form naturally', weight: { action: 1, practical: 1, results: 1 } }
    ]
  },
  {
    id: 'stu_social_15',
    phase: 'social_personality',
    type: 'reflection',
    question: 'How would your close friends describe you?',
    subtitle: 'Choose the most accurate description',
    options: [
      { id: 'a', text: 'Reliable and always there when needed', weight: { dependable: 2, loyal: 1, consistent: 1 } },
      { id: 'b', text: 'Fun and energizing to be around', weight: { energetic: 2, fun: 1, engaging: 1 } },
      { id: 'c', text: 'Thoughtful and good at giving advice', weight: { thoughtful: 2, wise: 1, helpful: 1 } },
      { id: 'd', text: 'Interesting and with unique perspectives', weight: { unique: 1, interesting: 1, different: 1 } }
    ]
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // PHASE 4: VALUES & MOTIVATION (30 questions)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  {
    id: 'stu_value_1',
    phase: 'values_motivation',
    type: 'forced_choice',
    question: 'What gives you the greatest sense of accomplishment?',
    subtitle: 'Choose what matters most',
    options: [
      { id: 'a', text: 'Achieving a challenging personal goal', weight: { achievement: 2, personal: 1, challenge: 1 } },
      { id: 'b', text: 'Helping others succeed or overcome difficulties', weight: { service: 2, helping: 1, others: 1 } },
      { id: 'c', text: 'Creating something new or original', weight: { creativity: 2, originality: 1, creation: 1 } },
      { id: 'd', text: 'Being recognized for excellence in your field', weight: { recognition: 2, excellence: 1, status: 1 } }
    ]
  },
  {
    id: 'stu_value_2',
    phase: 'values_motivation',
    type: 'forced_choice',
    question: 'Which of these values is most important in your life?',
    subtitle: 'Select your top value',
    options: [
      { id: 'a', text: 'Independence and freedom', weight: { autonomy: 2, freedom: 1, independence: 1 } },
      { id: 'b', text: 'Connection and relationships', weight: { belonging: 2, connection: 1, relationships: 1 } },
      { id: 'c', text: 'Achievement and success', weight: { success: 2, achievement: 1, winning: 1 } },
      { id: 'd', text: 'Security and stability', weight: { security: 2, stability: 1, safety: 1 } }
    ]
  },
  {
    id: 'stu_value_3',
    phase: 'values_motivation',
    type: 'scenario',
    question: 'You have the choice between a well-paying job you don\'t enjoy and a lower-paying job you love.',
    subtitle: 'How do you decide?',
    options: [
      { id: 'a', text: 'Choose passion—life is too short for work you dislike', weight: { passion: 2, fulfillment: 1, meaning: 1 } },
      { id: 'b', text: 'Choose security—practical needs come first', weight: { security: 2, practical: 1, responsible: 1 } },
      { id: 'c', text: 'Look for a third option that balances both', weight: { balanced: 1, creative: 1, exploring: 1 } },
      { id: 'd', text: 'Take the better pay now, pursue passion later', weight: { strategic: 1, deferred: 1, planning: 1 } }
    ]
  },
  {
    id: 'stu_value_4',
    phase: 'values_motivation',
    type: 'reflection',
    question: 'How important is it to you to make a difference in the world?',
    subtitle: 'Choose what reflects your view',
    options: [
      { id: 'a', text: 'Very important—it\'s a core part of who I am', weight: { purpose: 2, impact: 1, meaningful: 1 } },
      { id: 'b', text: 'Important but balanced with personal goals', weight: { balanced: 1, dual: 1, moderate: 1 } },
      { id: 'c', text: 'Nice to have but not a driving priority', weight: { secondary: 1, practical: 1, personal: 1 } },
      { id: 'd', text: 'I focus on my immediate sphere of influence', weight: { local: 1, immediate: 1, practical: 1 } }
    ]
  },
  {
    id: 'stu_value_5',
    phase: 'values_motivation',
    type: 'forced_choice',
    question: 'When facing a difficult choice, you typically prioritize:',
    subtitle: 'Select your primary consideration',
    options: [
      { id: 'a', text: 'What is right based on your principles', weight: { principled: 2, values: 1, integrity: 1 } },
      { id: 'b', text: 'What will produce the best outcomes', weight: { pragmatic: 2, results: 1, consequentialist: 1 } },
      { id: 'c', text: 'How it will affect people you care about', weight: { relational: 2, caring: 1, social: 1 } },
      { id: 'd', text: 'What feels right intuitively', weight: { intuitive: 2, feeling: 1, gut: 1 } }
    ]
  },
  {
    id: 'stu_value_6',
    phase: 'values_motivation',
    type: 'scenario',
    question: 'You are offered an opportunity that would advance your career but compromise your values slightly.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Decline—values are non-negotiable', weight: { principled: 2, integrity: 1, values: 1 } },
      { id: 'b', text: 'Depends on the specifics—evaluate carefully', weight: { analytical: 1, contextual: 1, thoughtful: 1 } },
      { id: 'c', text: 'Accept if the compromise is minor and temporary', weight: { pragmatic: 1, flexible: 1, realistic: 1 } },
      { id: 'd', text: 'Look for ways to take the opportunity while preserving values', weight: { creative: 1, both: 1, integrative: 1 } }
    ]
  },
  {
    id: 'stu_value_7',
    phase: 'values_motivation',
    type: 'reflection',
    question: 'What do you want to be known for?',
    subtitle: 'Choose your legacy preference',
    options: [
      { id: 'a', text: 'Excellence and achievement in your field', weight: { excellence: 2, achievement: 1, mastery: 1 } },
      { id: 'b', text: 'Kindness and positive impact on others', weight: { kindness: 2, impact: 1, service: 1 } },
      { id: 'c', text: 'Creativity and original contributions', weight: { creativity: 2, innovation: 1, originality: 1 } },
      { id: 'd', text: 'Integrity and being true to your values', weight: { integrity: 2, authenticity: 1, values: 1 } }
    ]
  },
  {
    id: 'stu_value_8',
    phase: 'values_motivation',
    type: 'forced_choice',
    question: 'How do you define success in life?',
    subtitle: 'Select your primary definition',
    options: [
      { id: 'a', text: 'Achieving goals and reaching your potential', weight: { achievement: 2, potential: 1, goals: 1 } },
      { id: 'b', text: 'Having strong relationships and belonging', weight: { relationships: 2, connection: 1, love: 1 } },
      { id: 'c', text: 'Living according to your values authentically', weight: { authenticity: 2, values: 1, meaning: 1 } },
      { id: 'd', text: 'Financial security and comfortable lifestyle', weight: { financial: 2, comfort: 1, security: 1 } }
    ]
  },
  {
    id: 'stu_value_9',
    phase: 'values_motivation',
    type: 'scenario',
    question: 'You have extra resources (time or money). How do you prefer to use them?',
    subtitle: 'Choose your preference',
    options: [
      { id: 'a', text: 'Invest in learning and personal development', weight: { growth: 2, development: 1, learning: 1 } },
      { id: 'b', text: 'Spend on experiences and enjoyment', weight: { experience: 2, enjoyment: 1, present: 1 } },
      { id: 'c', text: 'Save for future security', weight: { security: 2, future: 1, prudent: 1 } },
      { id: 'd', text: 'Help others or contribute to causes', weight: { giving: 2, service: 1, others: 1 } }
    ]
  },
  {
    id: 'stu_value_10',
    phase: 'values_motivation',
    type: 'reflection',
    question: 'How important is it to you to be unique or different from others?',
    subtitle: 'Choose what reflects your view',
    options: [
      { id: 'a', text: 'Very important—I value being distinctive', weight: { unique: 2, individual: 1, distinctive: 1 } },
      { id: 'b', text: 'Somewhat important—I balance fitting in and standing out', weight: { balanced: 1, moderate: 1, adaptive: 1 } },
      { id: 'c', text: 'Not very important—I prefer to fit in', weight: { conforming: 1, belonging: 1, fitting: 1 } },
      { id: 'd', text: 'I don\'t think about it—I just am who I am', weight: { natural: 1, unself_conscious: 1, authentic: 1 } }
    ]
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // PHASE 5: RESILIENCE & STRESS RESPONSE (25 questions)
  // ═══════════════════════════════════════════════════════════════════════════════
  
  {
    id: 'stu_stress_1',
    phase: 'resilience_stress',
    type: 'scenario',
    question: 'You face a significant setback—an important exam you failed or a rejection you received.',
    subtitle: 'How do you typically respond?',
    options: [
      { id: 'a', text: 'Analyze what went wrong and make a plan to improve', weight: { analytical: 1, proactive: 1, growth: 1 } },
      { id: 'b', text: 'Feel discouraged but bounce back relatively quickly', weight: { resilient: 1, balanced: 1, recovering: 1 } },
      { id: 'c', text: 'Talk to friends or family for support', weight: { social_support: 1, sharing: 1, connection: 1 } },
      { id: 'd', text: 'Need significant time to process and recover', weight: { processing: 1, sensitive: 1, slow: 1 } }
    ]
  },
  {
    id: 'stu_stress_2',
    phase: 'resilience_stress',
    type: 'forced_choice',
    question: 'When facing stress or pressure, you typically:',
    subtitle: 'Select your natural response',
    options: [
      { id: 'a', text: 'Feel energized and perform better', weight: { pressure_performer: 2, energized: 1, thriving: 1 } },
      { id: 'b', text: 'Manage it well but prefer calmer conditions', weight: { capable: 1, preference: 1, moderate: 1 } },
      { id: 'c', text: 'Feel anxious but push through', weight: { anxious: 1, persistent: 1, struggling: 1 } },
      { id: 'd', text: 'Need strategies to manage stress effectively', weight: { strategic: 1, managing: 1, coping: 1 } }
    ]
  },
  {
    id: 'stu_stress_3',
    phase: 'resilience_stress',
    type: 'reflection',
    question: 'How do you typically handle uncertainty about the future?',
    subtitle: 'Choose what describes you best',
    options: [
      { id: 'a', text: 'Find it exciting—possibilities are open', weight: { optimistic: 2, adventurous: 1, open: 1 } },
      { id: 'b', text: 'Accept it as part of life and focus on what I can control', weight: { accepting: 1, focused: 1, realistic: 1 } },
      { id: 'c', text: 'Feel anxious and try to plan for different scenarios', weight: { anxious: 1, planning: 1, preparing: 1 } },
      { id: 'd', text: 'Prefer to have clear plans and certainty', weight: { security: 1, clarity: 1, structured: 1 } }
    ]
  },
  {
    id: 'stu_stress_4',
    phase: 'resilience_stress',
    type: 'scenario',
    question: 'You made a mistake that affected others negatively.',
    subtitle: 'What is your response?',
    options: [
      { id: 'a', text: 'Apologize, take responsibility, and work to fix it', weight: { accountable: 2, responsible: 1, action: 1 } },
      { id: 'b', text: 'Feel terrible and replay it in your mind repeatedly', weight: { self_critical: 1, ruminating: 1, guilty: 1 } },
      { id: 'c', text: 'Learn from it but don\'t dwell on it too long', weight: { balanced: 1, learning: 1, moving: 1 } },
      { id: 'd', text: 'Seek reassurance from others that it\'s okay', weight: { reassurance: 1, social: 1, validation: 1 } }
    ]
  },
  {
    id: 'stu_stress_5',
    phase: 'resilience_stress',
    type: 'forced_choice',
    question: 'Your general outlook on life tends to be:',
    subtitle: 'Select what describes you best',
    options: [
      { id: 'a', text: 'Optimistic—things usually work out', weight: { optimistic: 2, positive: 1, hopeful: 1 } },
      { id: 'b', text: 'Realistic—I see both positives and negatives', weight: { realistic: 1, balanced: 1, grounded: 1 } },
      { id: 'c', text: 'Cautious—I prepare for things that could go wrong', weight: { cautious: 1, defensive: 1, prepared: 1 } },
      { id: 'd', text: 'Variable—depends on current circumstances', weight: { contextual: 1, variable: 1, responsive: 1 } }
    ]
  },
  {
    id: 'stu_stress_6',
    phase: 'resilience_stress',
    type: 'scenario',
    question: 'You are overwhelmed with responsibilities and feel you cannot manage everything.',
    subtitle: 'What do you do?',
    options: [
      { id: 'a', text: 'Prioritize ruthlessly and let some things go', weight: { prioritizing: 1, decisive: 1, practical: 1 } },
      { id: 'b', text: 'Ask for help or delegate to others', weight: { help_seeking: 1, collaborative: 1, resourceful: 1 } },
      { id: 'c', text: 'Push through—sleep less, work harder', weight: { persistent: 1, pushing: 1, sacrifice: 1 } },
      { id: 'd', text: 'Feel paralyzed and struggle to start anywhere', weight: { overwhelmed: 1, stuck: 1, anxious: 1 } }
    ]
  },
  {
    id: 'stu_stress_7',
    phase: 'resilience_stress',
    type: 'reflection',
    question: 'How would you describe your self-confidence?',
    subtitle: 'Choose the most accurate description',
    options: [
      { id: 'a', text: 'Generally strong—I believe in my abilities', weight: { confident: 2, self_assured: 1, positive: 1 } },
      { id: 'b', text: 'Varies depending on the area or situation', weight: { contextual: 1, variable: 1, situational: 1 } },
      { id: 'c', text: 'Something I\'m working on developing', weight: { developing: 1, growing: 1, aware: 1 } },
      { id: 'd', text: 'Often doubt myself even when capable', weight: { self_doubting: 1, uncertain: 1, modest: 1 } }
    ]
  },
  {
    id: 'stu_stress_8',
    phase: 'resilience_stress',
    type: 'scenario',
    question: 'Someone criticizes your work harshly and publicly.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Stay calm and address the feedback professionally', weight: { composed: 2, professional: 1, mature: 1 } },
      { id: 'b', text: 'Feel hurt and need time to recover', weight: { sensitive: 1, affected: 1, processing: 1 } },
      { id: 'c', text: 'Defend yourself and your work', weight: { defensive: 1, assertive: 1, protective: 1 } },
      { id: 'd', text: 'Evaluate if the criticism has merit before responding', weight: { analytical: 1, thoughtful: 1, measured: 1 } }
    ]
  },
  {
    id: 'stu_stress_9',
    phase: 'resilience_stress',
    type: 'forced_choice',
    question: 'When something bad happens, you tend to:',
    subtitle: 'Select your natural pattern',
    options: [
      { id: 'a', text: 'See it as temporary and specific to the situation', weight: { optimistic: 2, resilient: 1, bounded: 1 } },
      { id: 'b', text: 'Worry it signals a broader pattern or problem', weight: { generalizing: 1, worried: 1, pessimistic: 1 } },
      { id: 'c', text: 'Look for lessons and silver linings', weight: { learning: 1, positive: 1, growth: 1 } },
      { id: 'd', text: 'Accept it and move on without overthinking', weight: { accepting: 1, practical: 1, moving: 1 } }
    ]
  },
  {
    id: 'stu_stress_10',
    phase: 'resilience_stress',
    type: 'reflection',
    question: 'How do you typically recharge when feeling depleted?',
    subtitle: 'Choose your primary method',
    options: [
      { id: 'a', text: 'Time alone with quiet activities', weight: { solitary: 2, quiet: 1, reflective: 1 } },
      { id: 'b', text: 'Time with friends and social connection', weight: { social: 2, connection: 1, energizing: 1 } },
      { id: 'c', text: 'Physical activity or being in nature', weight: { active: 1, physical: 1, nature: 1 } },
      { id: 'd', text: 'Creative activities or hobbies', weight: { creative: 1, hobbies: 1, expression: 1 } }
    ]
  },

  // ═══════════════════════════════════════════════════════════════════════════════
  // EXTENDED QUESTION BANK - STUDENT CAREER DISCOVERY (Additional 100 questions)
  // ═══════════════════════════════════════════════════════════════════════════════

  // LEARNING STYLE EXTENSION
  {
    id: 'stu_learn_ext_1',
    phase: 'learning_cognitive',
    type: 'forced_choice',
    question: 'When you need to understand a complicated topic, you prefer to:',
    subtitle: 'Select your natural learning approach',
    options: [
      { id: 'a', text: 'Read detailed explanations at your own pace', weight: { reading: 2, independent: 1, thorough: 1 } },
      { id: 'b', text: 'Watch videos or demonstrations of the concept', weight: { visual: 2, multimedia: 1, observational: 1 } },
      { id: 'c', text: 'Discuss it with others and ask questions', weight: { social: 2, verbal: 1, collaborative: 1 } },
      { id: 'd', text: 'Try it hands-on and learn from experimentation', weight: { kinesthetic: 2, experiential: 1, active: 1 } }
    ]
  },
  {
    id: 'stu_learn_ext_2',
    phase: 'learning_cognitive',
    type: 'scenario',
    question: 'You are preparing for an important exam in a subject you find challenging.',
    subtitle: 'What is your study approach?',
    options: [
      { id: 'a', text: 'Create a detailed study schedule and stick to it', weight: { organized: 2, disciplined: 1, planning: 1 } },
      { id: 'b', text: 'Focus on understanding core concepts deeply', weight: { conceptual: 2, depth: 1, understanding: 1 } },
      { id: 'c', text: 'Practice with many sample problems and past papers', weight: { practical: 2, application: 1, repetition: 1 } },
      { id: 'd', text: 'Study with friends or in a group', weight: { social: 2, collaborative: 1, shared: 1 } }
    ]
  },
  {
    id: 'stu_learn_ext_3',
    phase: 'learning_cognitive',
    type: 'reflection',
    question: 'How do you prefer to receive instructions for a new task?',
    subtitle: 'Choose your preferred format',
    options: [
      { id: 'a', text: 'Written step-by-step instructions I can refer back to', weight: { written: 2, reference: 1, systematic: 1 } },
      { id: 'b', text: 'Verbal explanation with opportunity for questions', weight: { verbal: 2, interactive: 1, clarifying: 1 } },
      { id: 'c', text: 'Someone showing me how to do it first', weight: { demonstration: 2, visual: 1, modeling: 1 } },
      { id: 'd', text: 'Just the goal - I prefer figuring out the approach myself', weight: { independent: 2, autonomous: 1, discovery: 1 } }
    ]
  },
  {
    id: 'stu_learn_ext_4',
    phase: 'learning_cognitive',
    type: 'situational_judgement',
    question: 'A teacher explains something in a way that does not click for you.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would ask the teacher to explain it differently or find another resource.',
    weights: {
      strongly_disagree: { passive: 2, accepting: 1 },
      disagree: { hesitant: 1, conforming: 1 },
      agree: { proactive: 1, seeking: 1 },
      strongly_agree: { advocate: 2, resourceful: 1, persistent: 1 }
    }
  },
  {
    id: 'stu_learn_ext_5',
    phase: 'learning_cognitive',
    type: 'forced_choice',
    question: 'When working on a creative project, you typically:',
    subtitle: 'Select your natural approach',
    options: [
      { id: 'a', text: 'Plan everything out before starting', weight: { planning: 2, structured: 1, organized: 1 } },
      { id: 'b', text: 'Start with a general idea and develop as you go', weight: { flexible: 1, emergent: 1, creative: 1 } },
      { id: 'c', text: 'Look at examples for inspiration first', weight: { research: 1, modeling: 1, inspired: 1 } },
      { id: 'd', text: 'Brainstorm many ideas before choosing one', weight: { ideation: 2, exploratory: 1, creative: 1 } }
    ]
  },
  {
    id: 'stu_learn_ext_6',
    phase: 'learning_cognitive',
    type: 'scenario',
    question: 'You are assigned to a group project with classmates you do not know well.',
    subtitle: 'What role do you naturally take?',
    options: [
      { id: 'a', text: 'Organize the team and create a plan', weight: { leadership: 2, organizing: 1, initiative: 1 } },
      { id: 'b', text: 'Contribute ideas and build on others suggestions', weight: { collaborative: 2, creative: 1, supportive: 1 } },
      { id: 'c', text: 'Focus on your assigned part independently', weight: { independent: 1, focused: 1, task: 1 } },
      { id: 'd', text: 'Help keep the team harmonious and on track', weight: { harmonizing: 2, supportive: 1, facilitative: 1 } }
    ]
  },
  {
    id: 'stu_learn_ext_7',
    phase: 'learning_cognitive',
    type: 'reflection',
    question: 'Your attention span during lectures or classes is typically:',
    subtitle: 'Select the most honest description',
    options: [
      { id: 'a', text: 'Strong throughout - I can stay focused for the full session', weight: { focused: 2, sustained: 1, disciplined: 1 } },
      { id: 'b', text: 'Variable - depends on how interesting the topic is', weight: { interest_driven: 1, variable: 1, selective: 1 } },
      { id: 'c', text: 'Better in shorter bursts with breaks', weight: { chunked: 1, paced: 1, varied: 1 } },
      { id: 'd', text: 'I learn better through active participation than listening', weight: { active: 2, participatory: 1, kinesthetic: 1 } }
    ]
  },
  {
    id: 'stu_learn_ext_8',
    phase: 'learning_cognitive',
    type: 'forced_choice',
    question: 'When faced with a problem you have never seen before, you typically:',
    subtitle: 'Choose your natural first step',
    options: [
      { id: 'a', text: 'Break it down into smaller parts to understand', weight: { analytical: 2, systematic: 1, structured: 1 } },
      { id: 'b', text: 'Look for patterns or similarities to familiar problems', weight: { pattern: 2, connecting: 1, analogical: 1 } },
      { id: 'c', text: 'Try different approaches until something works', weight: { experimental: 2, trial: 1, persistent: 1 } },
      { id: 'd', text: 'Research how others have solved similar problems', weight: { research: 2, learning: 1, informed: 1 } }
    ]
  },
  {
    id: 'stu_learn_ext_9',
    phase: 'learning_cognitive',
    type: 'scenario',
    question: 'You have learned something that contradicts what you previously believed.',
    subtitle: 'How do you respond?',
    options: [
      { id: 'a', text: 'Examine the evidence and update your understanding', weight: { open: 2, learning: 1, growth: 1 } },
      { id: 'b', text: 'Seek additional sources to verify the new information', weight: { verifying: 1, thorough: 1, careful: 1 } },
      { id: 'c', text: 'Feel uncomfortable but gradually accept if evidence is strong', weight: { processing: 1, adaptive: 1, human: 1 } },
      { id: 'd', text: 'Enjoy discovering you were wrong - it means you learned something', weight: { growth_mindset: 2, curious: 1, positive: 1 } }
    ]
  },
  {
    id: 'stu_learn_ext_10',
    phase: 'learning_cognitive',
    type: 'situational_judgement',
    question: 'A topic you are studying seems completely irrelevant to your future goals.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would still try to learn it well because unexpected knowledge often becomes useful.',
    weights: {
      strongly_disagree: { narrow: 2, impatient: 1 },
      disagree: { focused: 1, selective: 1 },
      agree: { open: 1, growth: 1 },
      strongly_agree: { curious: 2, growth_mindset: 1, learning: 1 }
    }
  },

  // CAREER ORIENTATION EXTENSION
  {
    id: 'stu_career_ext_1',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'Which type of work environment sounds most appealing to you?',
    subtitle: 'Select the most attractive option',
    options: [
      { id: 'a', text: 'A creative studio where new ideas are constantly explored', weight: { creative: 2, innovative: 1, artistic: 1 } },
      { id: 'b', text: 'A research lab focused on discovering new knowledge', weight: { investigative: 2, research: 1, analytical: 1 } },
      { id: 'c', text: 'A dynamic business environment with growth opportunities', weight: { enterprising: 2, business: 1, growth: 1 } },
      { id: 'd', text: 'A service organization helping people directly', weight: { social: 2, helping: 1, service: 1 } }
    ]
  },
  {
    id: 'stu_career_ext_2',
    phase: 'career_orientation',
    type: 'scenario',
    question: 'You have the opportunity to shadow a professional for a day. Who would you choose?',
    subtitle: 'Select the most interesting option',
    options: [
      { id: 'a', text: 'A scientist working on breakthrough research', weight: { investigative: 2, science: 1, discovery: 1 } },
      { id: 'b', text: 'An entrepreneur building a startup', weight: { enterprising: 2, risk: 1, building: 1 } },
      { id: 'c', text: 'A designer or artist creating new works', weight: { artistic: 2, creative: 1, expression: 1 } },
      { id: 'd', text: 'A doctor, counselor, or teacher helping others', weight: { social: 2, helping: 1, caring: 1 } }
    ]
  },
  {
    id: 'stu_career_ext_3',
    phase: 'career_orientation',
    type: 'reflection',
    question: 'What would make a job feel truly satisfying to you?',
    subtitle: 'Select your primary motivator',
    options: [
      { id: 'a', text: 'Solving complex problems and creating new solutions', weight: { challenge: 2, problem_solving: 1, innovation: 1 } },
      { id: 'b', text: 'Making a positive difference in people lives', weight: { impact: 2, purpose: 1, meaning: 1 } },
      { id: 'c', text: 'Continuous learning and personal growth', weight: { growth: 2, learning: 1, mastery: 1 } },
      { id: 'd', text: 'Recognition and achievement of ambitious goals', weight: { achievement: 2, recognition: 1, success: 1 } }
    ]
  },
  {
    id: 'stu_career_ext_4',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'In your ideal career, you would primarily work:',
    subtitle: 'Select your preference',
    options: [
      { id: 'a', text: 'With ideas, data, and abstract concepts', weight: { conceptual: 2, analytical: 1, thinking: 1 } },
      { id: 'b', text: 'With people - teaching, helping, or leading', weight: { social: 2, people: 1, interpersonal: 1 } },
      { id: 'c', text: 'With things - building, creating, or making', weight: { practical: 2, hands_on: 1, tangible: 1 } },
      { id: 'd', text: 'With systems - organizing, managing, or optimizing', weight: { organizing: 2, systematic: 1, structured: 1 } }
    ]
  },
  {
    id: 'stu_career_ext_5',
    phase: 'career_orientation',
    type: 'scenario',
    question: 'You are choosing between two career paths - one stable but routine, one exciting but uncertain.',
    subtitle: 'Which would you lean toward?',
    options: [
      { id: 'a', text: 'The stable path - security and predictability matter to me', weight: { security: 2, stable: 1, risk_averse: 1 } },
      { id: 'b', text: 'The exciting path - growth and variety are worth the risk', weight: { adventurous: 2, growth: 1, risk_tolerant: 1 } },
      { id: 'c', text: 'I would try to find a balance between both', weight: { balanced: 1, pragmatic: 1, moderate: 1 } },
      { id: 'd', text: 'It depends on my financial situation and responsibilities', weight: { contextual: 1, responsible: 1, practical: 1 } }
    ]
  },
  {
    id: 'stu_career_ext_6',
    phase: 'career_orientation',
    type: 'situational_judgement',
    question: 'Your family expects you to pursue a career path that does not interest you.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would pursue my own interests even if it disappoints them.',
    weights: {
      strongly_disagree: { conforming: 2, family: 1 },
      disagree: { balanced: 1, respectful: 1 },
      agree: { independent: 1, self_directed: 1 },
      strongly_agree: { autonomous: 2, self_determined: 1, courageous: 1 }
    }
  },
  {
    id: 'stu_career_ext_7',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'What aspect of a job would you find most draining?',
    subtitle: 'Select the most unappealing option',
    options: [
      { id: 'a', text: 'Repetitive tasks without variety or challenge', weight: { variety_need: 2, stimulation: 1, boredom: 1 } },
      { id: 'b', text: 'Working alone without collaboration or interaction', weight: { social_need: 2, collaboration: 1, connection: 1 } },
      { id: 'c', text: 'Constant pressure and tight deadlines', weight: { stress_sensitive: 1, pace: 1, pressure: 1 } },
      { id: 'd', text: 'Work that does not align with your values', weight: { values_driven: 2, purpose: 1, meaning: 1 } }
    ]
  },
  {
    id: 'stu_career_ext_8',
    phase: 'career_orientation',
    type: 'reflection',
    question: 'When imagining your future career, what comes to mind first?',
    subtitle: 'Select your primary vision',
    options: [
      { id: 'a', text: 'Being an expert or leader in your field', weight: { mastery: 2, expertise: 1, authority: 1 } },
      { id: 'b', text: 'Creating something new or innovative', weight: { creative: 2, entrepreneurial: 1, building: 1 } },
      { id: 'c', text: 'Having a positive impact on others or society', weight: { impact: 2, purpose: 1, contribution: 1 } },
      { id: 'd', text: 'Having freedom, flexibility, and work-life balance', weight: { autonomy: 2, balance: 1, freedom: 1 } }
    ]
  },
  {
    id: 'stu_career_ext_9',
    phase: 'career_orientation',
    type: 'scenario',
    question: 'You have skills in both technology and communication. How would you combine them?',
    subtitle: 'Select the most appealing option',
    options: [
      { id: 'a', text: 'Build technical products that help people communicate better', weight: { product: 2, tech: 1, building: 1 } },
      { id: 'b', text: 'Explain technical concepts to non-technical audiences', weight: { bridging: 2, communication: 1, education: 1 } },
      { id: 'c', text: 'Lead technology teams and translate business needs', weight: { leadership: 2, managing: 1, translating: 1 } },
      { id: 'd', text: 'Create content or media using technical tools', weight: { creative: 2, media: 1, content: 1 } }
    ]
  },
  {
    id: 'stu_career_ext_10',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'Which of these achievements would feel most meaningful to you?',
    subtitle: 'Select the most important',
    options: [
      { id: 'a', text: 'Solving a problem that others could not', weight: { achievement: 2, competence: 1, challenge: 1 } },
      { id: 'b', text: 'Being recognized as the best in your field', weight: { recognition: 2, status: 1, excellence: 1 } },
      { id: 'c', text: 'Helping someone through a difficult time', weight: { caring: 2, impact: 1, connection: 1 } },
      { id: 'd', text: 'Creating something beautiful or meaningful', weight: { creative: 2, expression: 1, meaning: 1 } }
    ]
  },

  // SOCIAL & PERSONALITY EXTENSION
  {
    id: 'stu_social_ext_1',
    phase: 'social_personality',
    type: 'forced_choice',
    question: 'At a party or social gathering with many people you do not know, you typically:',
    subtitle: 'Select your natural behavior',
    options: [
      { id: 'a', text: 'Introduce yourself to new people and start conversations', weight: { extroverted: 2, initiating: 1, social: 1 } },
      { id: 'b', text: 'Wait for others to approach you or stick with people you know', weight: { reserved: 1, introverted: 1, selective: 1 } },
      { id: 'c', text: 'Observe for a while before joining conversations', weight: { observing: 1, cautious: 1, analytical: 1 } },
      { id: 'd', text: 'Find one person to have a deep conversation with', weight: { depth: 2, intimate: 1, meaningful: 1 } }
    ]
  },
  {
    id: 'stu_social_ext_2',
    phase: 'social_personality',
    type: 'scenario',
    question: 'A friend asks you to help them with something during your free time.',
    subtitle: 'What is your natural response?',
    options: [
      { id: 'a', text: 'Readily agree - helping friends is important', weight: { helpful: 2, giving: 1, friend: 1 } },
      { id: 'b', text: 'Consider if you have energy and check your schedule', weight: { balanced: 1, boundaried: 1, self_aware: 1 } },
      { id: 'c', text: 'Ask what they need before committing', weight: { practical: 1, informed: 1, careful: 1 } },
      { id: 'd', text: 'Depends on how close the friend is', weight: { selective: 1, prioritizing: 1, discerning: 1 } }
    ]
  },
  {
    id: 'stu_social_ext_3',
    phase: 'social_personality',
    type: 'reflection',
    question: 'How would your close friends describe your personality?',
    subtitle: 'Select the most likely description',
    options: [
      { id: 'a', text: 'Fun, energetic, and always up for adventure', weight: { energetic: 2, adventurous: 1, fun: 1 } },
      { id: 'b', text: 'Thoughtful, reliable, and good listener', weight: { thoughtful: 2, reliable: 1, listening: 1 } },
      { id: 'c', text: 'Creative, interesting, and unique perspective', weight: { creative: 2, unique: 1, interesting: 1 } },
      { id: 'd', text: 'Driven, focused, and knows what they want', weight: { driven: 2, focused: 1, determined: 1 } }
    ]
  },
  {
    id: 'stu_social_ext_4',
    phase: 'social_personality',
    type: 'situational_judgement',
    question: 'Someone in your group expresses an opinion you strongly disagree with.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would voice my disagreement respectfully even if it creates tension.',
    weights: {
      strongly_disagree: { conflict_averse: 2, agreeable: 1 },
      disagree: { cautious: 1, diplomatic: 1 },
      agree: { assertive: 1, principled: 1 },
      strongly_agree: { courageous: 2, authentic: 1, direct: 1 }
    }
  },
  {
    id: 'stu_social_ext_5',
    phase: 'social_personality',
    type: 'forced_choice',
    question: 'When making decisions, you tend to rely more on:',
    subtitle: 'Select your dominant style',
    options: [
      { id: 'a', text: 'Logic and objective analysis of facts', weight: { logical: 2, analytical: 1, objective: 1 } },
      { id: 'b', text: 'Your feelings and what feels right', weight: { feeling: 2, intuitive: 1, values: 1 } },
      { id: 'c', text: 'Advice from people you trust', weight: { collaborative: 1, social: 1, advised: 1 } },
      { id: 'd', text: 'A mix depending on the situation', weight: { balanced: 1, adaptive: 1, contextual: 1 } }
    ]
  },
  {
    id: 'stu_social_ext_6',
    phase: 'social_personality',
    type: 'scenario',
    question: 'You notice a classmate struggling and looking upset, but you do not know them well.',
    subtitle: 'What do you do?',
    options: [
      { id: 'a', text: 'Approach them and ask if they are okay', weight: { compassionate: 2, reaching: 1, caring: 1 } },
      { id: 'b', text: 'Give them space but be available if they reach out', weight: { respectful: 1, available: 1, boundaried: 1 } },
      { id: 'c', text: 'Tell someone who knows them better', weight: { practical: 1, delegating: 1, helping: 1 } },
      { id: 'd', text: 'Feel concerned but not sure how to help', weight: { empathetic: 1, uncertain: 1, caring: 1 } }
    ]
  },
  {
    id: 'stu_social_ext_7',
    phase: 'social_personality',
    type: 'reflection',
    question: 'Your ideal weekend would be:',
    subtitle: 'Select the most appealing option',
    options: [
      { id: 'a', text: 'Active social time with friends and activities', weight: { extroverted: 2, social: 1, active: 1 } },
      { id: 'b', text: 'Quiet time pursuing hobbies or interests', weight: { introverted: 2, solitary: 1, hobbies: 1 } },
      { id: 'c', text: 'A mix of both social and alone time', weight: { balanced: 1, ambivert: 1, varied: 1 } },
      { id: 'd', text: 'Something spontaneous and unplanned', weight: { spontaneous: 2, flexible: 1, adventurous: 1 } }
    ]
  },
  {
    id: 'stu_social_ext_8',
    phase: 'social_personality',
    type: 'forced_choice',
    question: 'In conversations, you typically:',
    subtitle: 'Select your natural tendency',
    options: [
      { id: 'a', text: 'Talk more than listen - you have lots to share', weight: { expressive: 2, talkative: 1, sharing: 1 } },
      { id: 'b', text: 'Listen more than talk - you prefer to absorb', weight: { listening: 2, receptive: 1, thoughtful: 1 } },
      { id: 'c', text: 'Balance depends on the topic and person', weight: { adaptive: 1, contextual: 1, balanced: 1 } },
      { id: 'd', text: 'Ask questions to understand others better', weight: { curious: 2, questioning: 1, interested: 1 } }
    ]
  },
  {
    id: 'stu_social_ext_9',
    phase: 'social_personality',
    type: 'scenario',
    question: 'You need to give feedback to a friend about something they could improve.',
    subtitle: 'How do you approach this?',
    options: [
      { id: 'a', text: 'Be direct and honest - they will appreciate the truth', weight: { direct: 2, honest: 1, straightforward: 1 } },
      { id: 'b', text: 'Frame it carefully to protect their feelings', weight: { diplomatic: 2, caring: 1, sensitive: 1 } },
      { id: 'c', text: 'Wait for them to ask before offering feedback', weight: { respectful: 1, passive: 1, waiting: 1 } },
      { id: 'd', text: 'Share through encouragement rather than criticism', weight: { positive: 1, supportive: 1, encouraging: 1 } }
    ]
  },
  {
    id: 'stu_social_ext_10',
    phase: 'social_personality',
    type: 'situational_judgement',
    question: 'Your close friend group has a disagreement and people are taking sides.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would try to help both sides understand each other rather than taking a side.',
    weights: {
      strongly_disagree: { loyal: 1, partisan: 1 },
      disagree: { loyal: 1, sided: 1 },
      agree: { peacemaking: 1, understanding: 1 },
      strongly_agree: { mediator: 2, harmonizing: 1, diplomatic: 1 }
    }
  },

  // VALUES & MOTIVATION EXTENSION
  {
    id: 'stu_values_ext_1',
    phase: 'values_motivation',
    type: 'forced_choice',
    question: 'Which of these motivates you most to work hard?',
    subtitle: 'Select your primary motivator',
    options: [
      { id: 'a', text: 'Reaching personal goals and proving yourself', weight: { achievement: 2, proving: 1, goals: 1 } },
      { id: 'b', text: 'Making people you care about proud', weight: { approval: 2, family: 1, external: 1 } },
      { id: 'c', text: 'Learning and becoming better at something', weight: { mastery: 2, growth: 1, intrinsic: 1 } },
      { id: 'd', text: 'Contributing to something meaningful', weight: { purpose: 2, meaning: 1, contribution: 1 } }
    ]
  },
  {
    id: 'stu_values_ext_2',
    phase: 'values_motivation',
    type: 'scenario',
    question: 'You have the choice between a well-paying job and a job aligned with your passion.',
    subtitle: 'How do you decide?',
    options: [
      { id: 'a', text: 'Choose the well-paying job - financial security comes first', weight: { security: 2, financial: 1, practical: 1 } },
      { id: 'b', text: 'Choose passion - you will find a way to make it work', weight: { passion: 2, fulfillment: 1, risk: 1 } },
      { id: 'c', text: 'Look for ways to combine both or compromise', weight: { balanced: 1, creative: 1, optimizing: 1 } },
      { id: 'd', text: 'Depends on the specific situation and numbers', weight: { analytical: 1, contextual: 1, practical: 1 } }
    ]
  },
  {
    id: 'stu_values_ext_3',
    phase: 'values_motivation',
    type: 'reflection',
    question: 'What would you most regret not doing in your life?',
    subtitle: 'Select the most important',
    options: [
      { id: 'a', text: 'Not achieving your full potential', weight: { achievement: 2, potential: 1, excellence: 1 } },
      { id: 'b', text: 'Not making a positive difference', weight: { impact: 2, contribution: 1, meaning: 1 } },
      { id: 'c', text: 'Not experiencing enough adventures and variety', weight: { experience: 2, adventure: 1, variety: 1 } },
      { id: 'd', text: 'Not spending enough time with loved ones', weight: { relationships: 2, family: 1, connection: 1 } }
    ]
  },
  {
    id: 'stu_values_ext_4',
    phase: 'values_motivation',
    type: 'situational_judgement',
    question: 'Achieving success requires compromising something you believe in.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would reconsider that definition of success rather than compromise my values.',
    weights: {
      strongly_disagree: { pragmatic: 2, flexible: 1 },
      disagree: { moderate: 1, balanced: 1 },
      agree: { principled: 1, values: 1 },
      strongly_agree: { integrity: 2, principled: 2, values_driven: 1 }
    }
  },
  {
    id: 'stu_values_ext_5',
    phase: 'values_motivation',
    type: 'forced_choice',
    question: 'How important is financial wealth in your definition of a successful life?',
    subtitle: 'Select the most accurate statement',
    options: [
      { id: 'a', text: 'Very important - financial success is a primary goal', weight: { wealth: 2, financial: 1, material: 1 } },
      { id: 'b', text: 'Important - but not at the expense of other things', weight: { balanced: 1, moderate: 1, practical: 1 } },
      { id: 'c', text: 'Somewhat important - enough to be comfortable is sufficient', weight: { sufficiency: 1, contentment: 1, balanced: 1 } },
      { id: 'd', text: 'Not very important - other things matter more', weight: { purpose: 1, meaning: 1, non_material: 1 } }
    ]
  },
  {
    id: 'stu_values_ext_6',
    phase: 'values_motivation',
    type: 'scenario',
    question: 'You succeed at something but realize you received unfair help that others did not have.',
    subtitle: 'How do you feel?',
    options: [
      { id: 'a', text: 'Uncomfortable - you prefer to earn success fairly', weight: { fair: 2, principled: 1, honest: 1 } },
      { id: 'b', text: 'Grateful - you take the opportunities you are given', weight: { pragmatic: 1, grateful: 1, accepting: 1 } },
      { id: 'c', text: 'Motivated to help others get similar opportunities', weight: { equity: 2, giving: 1, fair: 1 } },
      { id: 'd', text: 'Would disclose the advantage to level the field', weight: { transparent: 2, honest: 1, fair: 1 } }
    ]
  },
  {
    id: 'stu_values_ext_7',
    phase: 'values_motivation',
    type: 'reflection',
    question: 'What best describes your relationship with competition?',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'I thrive on competition - it brings out my best', weight: { competitive: 2, driven: 1, motivated: 1 } },
      { id: 'b', text: 'I compete primarily with myself to improve', weight: { self_improving: 2, internal: 1, growth: 1 } },
      { id: 'c', text: 'I prefer collaboration over competition', weight: { collaborative: 2, cooperative: 1, team: 1 } },
      { id: 'd', text: 'Competition depends on what is at stake', weight: { selective: 1, contextual: 1, strategic: 1 } }
    ]
  },
  {
    id: 'stu_values_ext_8',
    phase: 'values_motivation',
    type: 'forced_choice',
    question: 'How do you typically set goals for yourself?',
    subtitle: 'Select your natural approach',
    options: [
      { id: 'a', text: 'Ambitious goals that push you beyond comfort', weight: { ambitious: 2, stretching: 1, challenging: 1 } },
      { id: 'b', text: 'Realistic goals you are confident you can achieve', weight: { realistic: 1, achievable: 1, confident: 1 } },
      { id: 'c', text: 'Flexible direction rather than specific targets', weight: { flexible: 1, directional: 1, adaptive: 1 } },
      { id: 'd', text: 'Short-term goals with long-term vision', weight: { structured: 1, staged: 1, planned: 1 } }
    ]
  },
  {
    id: 'stu_values_ext_9',
    phase: 'values_motivation',
    type: 'scenario',
    question: 'You have the opportunity to take credit for a group success that was mostly your work.',
    subtitle: 'What do you do?',
    options: [
      { id: 'a', text: 'Share credit equally with the group', weight: { humble: 2, team: 1, fair: 1 } },
      { id: 'b', text: 'Acknowledge the group but clarify your contribution', weight: { balanced: 1, honest: 1, fair: 1 } },
      { id: 'c', text: 'Take the credit - your contribution earned it', weight: { claiming: 1, deserving: 1, individual: 1 } },
      { id: 'd', text: 'Let the work speak for itself without claiming', weight: { modest: 1, quiet: 1, humble: 1 } }
    ]
  },
  {
    id: 'stu_values_ext_10',
    phase: 'values_motivation',
    type: 'situational_judgement',
    question: 'Someone suggests taking a shortcut that is technically allowed but feels unethical.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would refuse even if it means more work or a worse outcome.',
    weights: {
      strongly_disagree: { pragmatic: 2, outcome: 1 },
      disagree: { flexible: 1, moderate: 1 },
      agree: { principled: 1, ethical: 1 },
      strongly_agree: { integrity: 2, values: 2, ethical: 1 }
    }
  },

  // RESILIENCE & STRESS EXTENSION
  {
    id: 'stu_stress_ext_1',
    phase: 'resilience_stress',
    type: 'forced_choice',
    question: 'When you make a significant mistake, your first reaction is usually:',
    subtitle: 'Select your natural response',
    options: [
      { id: 'a', text: 'Fix it quickly and move on', weight: { action: 2, forward: 1, efficient: 1 } },
      { id: 'b', text: 'Understand what went wrong before fixing', weight: { analytical: 2, learning: 1, reflective: 1 } },
      { id: 'c', text: 'Feel bad but use it as motivation to do better', weight: { motivated: 1, emotional: 1, growth: 1 } },
      { id: 'd', text: 'Talk to someone about what happened', weight: { processing: 1, social: 1, sharing: 1 } }
    ]
  },
  {
    id: 'stu_stress_ext_2',
    phase: 'resilience_stress',
    type: 'scenario',
    question: 'You have worked very hard on something, but the result is disappointing.',
    subtitle: 'How do you process this?',
    options: [
      { id: 'a', text: 'Focus on what you learned and how to improve', weight: { growth: 2, learning: 1, forward: 1 } },
      { id: 'b', text: 'Take time to feel disappointed before analyzing', weight: { emotional: 1, processing: 1, feeling: 1 } },
      { id: 'c', text: 'Remind yourself that effort matters more than outcomes', weight: { process: 1, values: 1, perspective: 1 } },
      { id: 'd', text: 'Get feedback from others on what could be better', weight: { feedback: 2, collaborative: 1, learning: 1 } }
    ]
  },
  {
    id: 'stu_stress_ext_3',
    phase: 'resilience_stress',
    type: 'reflection',
    question: 'When facing a very stressful situation, you typically:',
    subtitle: 'Select your natural response',
    options: [
      { id: 'a', text: 'Stay calm and focus on what you can control', weight: { composed: 2, focused: 1, controlled: 1 } },
      { id: 'b', text: 'Feel anxious but push through anyway', weight: { persistent: 1, pushing: 1, determined: 1 } },
      { id: 'c', text: 'Seek support from others who can help', weight: { support_seeking: 2, collaborative: 1, connected: 1 } },
      { id: 'd', text: 'Take breaks to manage your stress levels', weight: { self_care: 2, pacing: 1, aware: 1 } }
    ]
  },
  {
    id: 'stu_stress_ext_4',
    phase: 'resilience_stress',
    type: 'situational_judgement',
    question: 'You have been rejected from something you really wanted.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would quickly look for alternative paths rather than dwelling on the rejection.',
    weights: {
      strongly_disagree: { processing: 1, dwelling: 1 },
      disagree: { feeling: 1, slow: 1 },
      agree: { forward: 1, adaptive: 1 },
      strongly_agree: { resilient: 2, forward: 1, adaptive: 1 }
    }
  },
  {
    id: 'stu_stress_ext_5',
    phase: 'resilience_stress',
    type: 'forced_choice',
    question: 'Your general outlook on challenges and obstacles is:',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Obstacles are opportunities in disguise', weight: { optimistic: 2, growth: 1, positive: 1 } },
      { id: 'b', text: 'Obstacles are tests to be overcome', weight: { determined: 1, fighter: 1, persistent: 1 } },
      { id: 'c', text: 'Obstacles require careful navigation', weight: { strategic: 1, cautious: 1, thoughtful: 1 } },
      { id: 'd', text: 'Obstacles can be overwhelming but you manage', weight: { realistic: 1, coping: 1, honest: 1 } }
    ]
  },
  {
    id: 'stu_stress_ext_6',
    phase: 'resilience_stress',
    type: 'scenario',
    question: 'Multiple things are going wrong in your life at the same time.',
    subtitle: 'How do you cope?',
    options: [
      { id: 'a', text: 'Prioritize and tackle one thing at a time', weight: { organized: 2, focused: 1, systematic: 1 } },
      { id: 'b', text: 'Reach out to your support network', weight: { connected: 2, support: 1, relationships: 1 } },
      { id: 'c', text: 'Keep perspective - remember that things pass', weight: { perspective: 2, philosophical: 1, wise: 1 } },
      { id: 'd', text: 'Focus on self-care to maintain your capacity', weight: { self_care: 2, sustainable: 1, healthy: 1 } }
    ]
  },
  {
    id: 'stu_stress_ext_7',
    phase: 'resilience_stress',
    type: 'reflection',
    question: 'How quickly do you typically recover from setbacks?',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Very quickly - I bounce back fast', weight: { resilient: 2, quick: 1, bouncing: 1 } },
      { id: 'b', text: 'Moderate - I need some time but recover well', weight: { moderate: 1, processing: 1, recovering: 1 } },
      { id: 'c', text: 'Slower - setbacks affect me significantly for a while', weight: { affected: 1, sensitive: 1, deeper: 1 } },
      { id: 'd', text: 'Variable - depends on the type of setback', weight: { contextual: 1, variable: 1, depends: 1 } }
    ]
  },
  {
    id: 'stu_stress_ext_8',
    phase: 'resilience_stress',
    type: 'forced_choice',
    question: 'When you feel overwhelmed, you are most likely to:',
    subtitle: 'Select your typical response',
    options: [
      { id: 'a', text: 'Push harder to power through', weight: { pushing: 1, determined: 1, powering: 1 } },
      { id: 'b', text: 'Take a step back to regroup', weight: { strategic: 2, pausing: 1, thoughtful: 1 } },
      { id: 'c', text: 'Seek help or delegate some responsibilities', weight: { asking: 2, support: 1, resourceful: 1 } },
      { id: 'd', text: 'Reduce expectations and simplify', weight: { adjusting: 1, realistic: 1, simplifying: 1 } }
    ]
  },
  {
    id: 'stu_stress_ext_9',
    phase: 'resilience_stress',
    type: 'scenario',
    question: 'You have to do something that makes you very nervous.',
    subtitle: 'How do you prepare?',
    options: [
      { id: 'a', text: 'Practice extensively until you feel confident', weight: { preparation: 2, practice: 1, thorough: 1 } },
      { id: 'b', text: 'Visualize success and talk yourself up', weight: { mental: 2, positive: 1, self_talk: 1 } },
      { id: 'c', text: 'Accept the nervousness and do it anyway', weight: { acceptance: 2, courage: 1, action: 1 } },
      { id: 'd', text: 'Focus on the worst case being survivable', weight: { perspective: 1, rational: 1, managing: 1 } }
    ]
  },
  {
    id: 'stu_stress_ext_10',
    phase: 'resilience_stress',
    type: 'situational_judgement',
    question: 'You are going through a difficult personal situation while facing demanding commitments.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would be transparent about needing support rather than struggling alone.',
    weights: {
      strongly_disagree: { isolated: 2, private: 1 },
      disagree: { independent: 1, self_reliant: 1 },
      agree: { open: 1, supported: 1 },
      strongly_agree: { transparent: 2, connected: 1, supported: 1 }
    }
  },

  // TEMPORAL & SELF-AWARENESS EXTENSION
  {
    id: 'stu_temporal_ext_1',
    phase: 'temporal_patterns',
    type: 'forced_choice',
    question: 'When do you feel most mentally sharp and productive?',
    subtitle: 'Select your peak time',
    options: [
      { id: 'a', text: 'Early morning - I am a morning person', weight: { morning: 2, early: 1, dawn: 1 } },
      { id: 'b', text: 'Midday to afternoon - I need time to warm up', weight: { afternoon: 2, midday: 1, gradual: 1 } },
      { id: 'c', text: 'Evening or night - I come alive later', weight: { evening: 2, night: 1, late: 1 } },
      { id: 'd', text: 'Variable - depends on sleep and other factors', weight: { variable: 1, contextual: 1, flexible: 1 } }
    ]
  },
  {
    id: 'stu_temporal_ext_2',
    phase: 'temporal_patterns',
    type: 'scenario',
    question: 'You need to make an important decision with incomplete information.',
    subtitle: 'What is your approach?',
    options: [
      { id: 'a', text: 'Gather all available information and analyze systematically', weight: { analytical: 2, thorough: 1, systematic: 1 } },
      { id: 'b', text: 'Trust your gut feeling and instincts', weight: { intuitive: 2, trusting: 1, instinct: 1 } },
      { id: 'c', text: 'Seek advice from people who have faced similar decisions', weight: { consulting: 2, wisdom: 1, learning: 1 } },
      { id: 'd', text: 'Make a provisional decision and adjust if needed', weight: { adaptive: 2, iterative: 1, flexible: 1 } }
    ]
  },
  {
    id: 'stu_temporal_ext_3',
    phase: 'temporal_patterns',
    type: 'reflection',
    question: 'How would you describe your relationship with time and deadlines?',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Very punctual and organized - deadlines drive me', weight: { punctual: 2, organized: 1, deadline: 1 } },
      { id: 'b', text: 'Usually on time but sometimes cut it close', weight: { moderate: 1, reasonable: 1, typical: 1 } },
      { id: 'c', text: 'Work best under pressure near deadlines', weight: { pressure: 1, last_minute: 1, adrenaline: 1 } },
      { id: 'd', text: 'Prefer flexible timelines over strict deadlines', weight: { flexible: 1, adaptive: 1, freedom: 1 } }
    ]
  },
  {
    id: 'stu_temporal_ext_4',
    phase: 'temporal_patterns',
    type: 'forced_choice',
    question: 'Your thinking style is best described as:',
    subtitle: 'Select your dominant pattern',
    options: [
      { id: 'a', text: 'Sequential and step-by-step logical', weight: { sequential: 2, logical: 1, linear: 1 } },
      { id: 'b', text: 'Holistic and big-picture oriented', weight: { holistic: 2, global: 1, conceptual: 1 } },
      { id: 'c', text: 'Creative and making unusual connections', weight: { creative: 2, associative: 1, divergent: 1 } },
      { id: 'd', text: 'Practical and focused on what works', weight: { practical: 2, pragmatic: 1, applied: 1 } }
    ]
  },
  {
    id: 'stu_temporal_ext_5',
    phase: 'temporal_patterns',
    type: 'scenario',
    question: 'Looking back at your life so far, what pattern do you notice?',
    subtitle: 'Select the most accurate',
    options: [
      { id: 'a', text: 'Steady progress toward clear goals', weight: { directed: 2, focused: 1, progressive: 1 } },
      { id: 'b', text: 'Exploration and trying many different things', weight: { exploring: 2, varied: 1, discovering: 1 } },
      { id: 'c', text: 'Ups and downs with important lessons learned', weight: { learning: 1, growth: 1, resilient: 1 } },
      { id: 'd', text: 'Following opportunities as they arose', weight: { opportunistic: 1, adaptive: 1, flowing: 1 } }
    ]
  },
  {
    id: 'stu_temporal_ext_6',
    phase: 'temporal_patterns',
    type: 'situational_judgement',
    question: 'You discover a natural talent for something you never considered pursuing.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would seriously consider changing direction to develop this talent.',
    weights: {
      strongly_disagree: { fixed: 2, committed: 1 },
      disagree: { focused: 1, stable: 1 },
      agree: { open: 1, exploring: 1 },
      strongly_agree: { adaptive: 2, open: 1, opportunity: 1 }
    }
  },
  {
    id: 'stu_temporal_ext_7',
    phase: 'temporal_patterns',
    type: 'reflection',
    question: 'How aware are you of your own emotions as you experience them?',
    subtitle: 'Select the most accurate description',
    options: [
      { id: 'a', text: 'Very aware - I notice emotions clearly as they arise', weight: { aware: 2, tuned: 1, mindful: 1 } },
      { id: 'b', text: 'Moderately aware - I understand my emotions with reflection', weight: { moderate: 1, reflective: 1, processing: 1 } },
      { id: 'c', text: 'Sometimes surprised by my emotional reactions', weight: { surprised: 1, less_aware: 1, delayed: 1 } },
      { id: 'd', text: 'I tend to think more than feel in most situations', weight: { thinking: 1, cerebral: 1, logical: 1 } }
    ]
  },
  {
    id: 'stu_temporal_ext_8',
    phase: 'temporal_patterns',
    type: 'forced_choice',
    question: 'When thinking about your future, you primarily feel:',
    subtitle: 'Select your dominant feeling',
    options: [
      { id: 'a', text: 'Excited about possibilities and opportunities', weight: { optimistic: 2, excited: 1, positive: 1 } },
      { id: 'b', text: 'Determined to achieve specific goals', weight: { determined: 2, focused: 1, driven: 1 } },
      { id: 'c', text: 'Curious about what will unfold', weight: { curious: 2, open: 1, wondering: 1 } },
      { id: 'd', text: 'Some uncertainty but confidence in your ability to adapt', weight: { adaptive: 1, confident: 1, realistic: 1 } }
    ]
  },
  {
    id: 'stu_temporal_ext_9',
    phase: 'temporal_patterns',
    type: 'scenario',
    question: 'You have achieved a significant goal you worked hard for.',
    subtitle: 'What is your typical response?',
    options: [
      { id: 'a', text: 'Celebrate and enjoy the accomplishment fully', weight: { celebrating: 2, savoring: 1, present: 1 } },
      { id: 'b', text: 'Quickly focus on the next goal or challenge', weight: { driven: 2, forward: 1, ambitious: 1 } },
      { id: 'c', text: 'Reflect on the journey and what you learned', weight: { reflective: 2, learning: 1, grateful: 1 } },
      { id: 'd', text: 'Share the success with others who supported you', weight: { sharing: 2, connected: 1, grateful: 1 } }
    ]
  },
  {
    id: 'stu_temporal_ext_10',
    phase: 'temporal_patterns',
    type: 'situational_judgement',
    question: 'Someone gives you unexpected negative feedback about yourself.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would take time to consider whether it contains useful truth.',
    weights: {
      strongly_disagree: { defensive: 2, closed: 1 },
      disagree: { resistant: 1, protective: 1 },
      agree: { open: 1, considering: 1 },
      strongly_agree: { growth_mindset: 2, humble: 1, learning: 1 }
    }
  }
];

// ADDITIONAL DEPTH QUESTIONS FOR STUDENTS
export const STUDENT_DEPTH_QUESTIONS = [
  {
    id: 'stu_depth_1',
    phase: 'learning_cognitive',
    type: 'forced_choice',
    question: 'When you have to memorize information, which method works best for you?',
    subtitle: 'Select your most effective approach',
    options: [
      { id: 'a', text: 'Creating associations or stories around the information', weight: { associative: 2, creative: 1, narrative: 1 } },
      { id: 'b', text: 'Repeated review and practice over time', weight: { repetition: 2, disciplined: 1, consistent: 1 } },
      { id: 'c', text: 'Teaching or explaining it to someone else', weight: { teaching: 2, social: 1, processing: 1 } },
      { id: 'd', text: 'Organizing information into visual structures', weight: { visual: 2, organized: 1, spatial: 1 } }
    ]
  },
  {
    id: 'stu_depth_2',
    phase: 'career_orientation',
    type: 'scenario',
    question: 'You are offered an internship in a field different from what you planned to study.',
    subtitle: 'How do you view this opportunity?',
    options: [
      { id: 'a', text: 'Exciting chance to explore something new', weight: { exploratory: 2, curious: 1, open: 1 } },
      { id: 'b', text: 'Distraction from your focused path', weight: { focused: 2, directed: 1, determined: 1 } },
      { id: 'c', text: 'Valuable for building transferable skills', weight: { pragmatic: 2, strategic: 1, development: 1 } },
      { id: 'd', text: 'Worth exploring if aligned with broader interests', weight: { balanced: 1, thoughtful: 1, considered: 1 } }
    ]
  },
  {
    id: 'stu_depth_3',
    phase: 'social_personality',
    type: 'situational_judgement',
    question: 'A friend copies your work without permission and submits it as their own.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would confront them about it even if it risks the friendship.',
    weights: {
      strongly_disagree: { conflict_averse: 2, preserving: 1 },
      disagree: { diplomatic: 1, careful: 1 },
      agree: { principled: 1, honest: 1 },
      strongly_agree: { principled: 2, integrity: 1, honest: 1 }
    }
  },
  {
    id: 'stu_depth_4',
    phase: 'values_motivation',
    type: 'reflection',
    question: 'What kind of legacy would you like to leave behind?',
    subtitle: 'Select the most important',
    options: [
      { id: 'a', text: 'Being remembered as exceptionally talented or successful', weight: { achievement: 2, excellence: 1, recognition: 1 } },
      { id: 'b', text: 'Having made a positive difference in many lives', weight: { impact: 2, service: 1, meaning: 1 } },
      { id: 'c', text: 'Creating something that outlasts you', weight: { creation: 2, lasting: 1, contribution: 1 } },
      { id: 'd', text: 'Being remembered as a good person by those who knew you', weight: { character: 2, relationship: 1, integrity: 1 } }
    ]
  },
  {
    id: 'stu_depth_5',
    phase: 'resilience_stress',
    type: 'forced_choice',
    question: 'When you feel like giving up on a difficult goal, what keeps you going?',
    subtitle: 'Select your primary motivator',
    options: [
      { id: 'a', text: 'Not wanting to disappoint yourself or others', weight: { commitment: 1, external: 1, responsibility: 1 } },
      { id: 'b', text: 'Believing that persistence leads to breakthrough', weight: { optimistic: 2, growth: 1, determined: 1 } },
      { id: 'c', text: 'Remembering why the goal matters to you', weight: { purpose: 2, intrinsic: 1, meaning: 1 } },
      { id: 'd', text: 'Knowing that quitting would be worse than struggling', weight: { determined: 2, persistent: 1, grit: 1 } }
    ]
  },
  {
    id: 'stu_depth_6',
    phase: 'learning_cognitive',
    type: 'scenario',
    question: 'You must learn a complex skill with minimal guidance or instruction.',
    subtitle: 'How do you approach this?',
    options: [
      { id: 'a', text: 'Find tutorials, guides, or courses online', weight: { resourceful: 2, self_directed: 1, research: 1 } },
      { id: 'b', text: 'Start experimenting and learn through trial and error', weight: { experimental: 2, hands_on: 1, action: 1 } },
      { id: 'c', text: 'Find someone who has the skill to mentor you', weight: { social: 2, mentored: 1, guided: 1 } },
      { id: 'd', text: 'Break it into small pieces and master each systematically', weight: { systematic: 2, structured: 1, methodical: 1 } }
    ]
  },
  {
    id: 'stu_depth_7',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'Which statement best describes your career planning approach?',
    subtitle: 'Select the most accurate',
    options: [
      { id: 'a', text: 'I have a clear 5-10 year vision I am working toward', weight: { planned: 2, directed: 1, long_term: 1 } },
      { id: 'b', text: 'I have general direction but stay open to opportunities', weight: { flexible: 2, open: 1, adaptive: 1 } },
      { id: 'c', text: 'I am still exploring and discovering what I want', weight: { exploring: 2, discovering: 1, curious: 1 } },
      { id: 'd', text: 'I focus on developing skills that will serve me anywhere', weight: { versatile: 2, skill_building: 1, adaptive: 1 } }
    ]
  },
  {
    id: 'stu_depth_8',
    phase: 'social_personality',
    type: 'reflection',
    question: 'In team settings, the role you most naturally gravitate toward is:',
    subtitle: 'Select your typical role',
    options: [
      { id: 'a', text: 'The idea person who comes up with creative solutions', weight: { creative: 2, ideating: 1, innovator: 1 } },
      { id: 'b', text: 'The organizer who keeps things on track', weight: { organizing: 2, managing: 1, structured: 1 } },
      { id: 'c', text: 'The peacemaker who ensures everyone is heard', weight: { harmonizing: 2, inclusive: 1, diplomatic: 1 } },
      { id: 'd', text: 'The doer who makes sure things actually get done', weight: { executing: 2, action: 1, reliable: 1 } }
    ]
  },
  {
    id: 'stu_depth_9',
    phase: 'values_motivation',
    type: 'situational_judgement',
    question: 'You could take a shortcut that would help you succeed but might disadvantage others.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would avoid the shortcut even if no one would ever know.',
    weights: {
      strongly_disagree: { competitive: 2, pragmatic: 1 },
      disagree: { flexible: 1, situational: 1 },
      agree: { principled: 1, fair: 1 },
      strongly_agree: { integrity: 2, principled: 2, ethical: 1 }
    }
  },
  {
    id: 'stu_depth_10',
    phase: 'resilience_stress',
    type: 'scenario',
    question: 'You receive harsh criticism on work you put significant effort into.',
    subtitle: 'What is your emotional journey?',
    options: [
      { id: 'a', text: 'Initial hurt followed by quick focus on improvement', weight: { resilient: 2, growth: 1, action: 1 } },
      { id: 'b', text: 'Question whether the criticism is fair or justified', weight: { analytical: 1, evaluating: 1, questioning: 1 } },
      { id: 'c', text: 'Feel discouraged and need time to recover motivation', weight: { affected: 1, sensitive: 1, processing: 1 } },
      { id: 'd', text: 'Separate the feedback from self-worth and learn from it', weight: { mature: 2, detached: 1, learning: 1 } }
    ]
  },
  {
    id: 'stu_depth_11',
    phase: 'learning_cognitive',
    type: 'forced_choice',
    question: 'Your preferred balance between breadth and depth in learning is:',
    subtitle: 'Select your preference',
    options: [
      { id: 'a', text: 'Deep expertise in one or two areas', weight: { specialist: 2, depth: 1, focused: 1 } },
      { id: 'b', text: 'Broad knowledge across many subjects', weight: { generalist: 2, breadth: 1, curious: 1 } },
      { id: 'c', text: 'Deep expertise with broad awareness around it', weight: { t_shaped: 2, balanced: 1, strategic: 1 } },
      { id: 'd', text: 'Whatever serves your current goals and interests', weight: { pragmatic: 1, adaptive: 1, goal_oriented: 1 } }
    ]
  },
  {
    id: 'stu_depth_12',
    phase: 'career_orientation',
    type: 'reflection',
    question: 'What aspect of future work worries you most?',
    subtitle: 'Select your primary concern',
    options: [
      { id: 'a', text: 'Not finding work that feels meaningful', weight: { meaning: 2, purpose: 1, fulfillment: 1 } },
      { id: 'b', text: 'Not achieving the success or recognition you want', weight: { achievement: 2, recognition: 1, status: 1 } },
      { id: 'c', text: 'Not being able to maintain work-life balance', weight: { balance: 2, wellbeing: 1, lifestyle: 1 } },
      { id: 'd', text: 'Not being competitive or skilled enough', weight: { competence: 2, adequacy: 1, skill: 1 } }
    ]
  },
  {
    id: 'stu_depth_13',
    phase: 'social_personality',
    type: 'scenario',
    question: 'You have to give negative feedback to a friend about their work.',
    subtitle: 'How do you approach this?',
    options: [
      { id: 'a', text: 'Be direct but kind - they need honesty to improve', weight: { honest: 2, caring: 1, direct: 1 } },
      { id: 'b', text: 'Focus on positives first, then gently mention areas to improve', weight: { diplomatic: 2, balanced: 1, supportive: 1 } },
      { id: 'c', text: 'Ask questions that help them discover issues themselves', weight: { facilitative: 2, coaching: 1, subtle: 1 } },
      { id: 'd', text: 'Avoid giving feedback unless directly asked', weight: { reserved: 1, cautious: 1, passive: 1 } }
    ]
  },
  {
    id: 'stu_depth_14',
    phase: 'values_motivation',
    type: 'forced_choice',
    question: 'How do you define personal success?',
    subtitle: 'Select the most important element',
    options: [
      { id: 'a', text: 'Achieving goals I set for myself', weight: { achievement: 2, goals: 1, determined: 1 } },
      { id: 'b', text: 'Growing and becoming the best version of myself', weight: { growth: 2, self_improvement: 1, development: 1 } },
      { id: 'c', text: 'Being happy and content with my life', weight: { contentment: 2, happiness: 1, fulfilled: 1 } },
      { id: 'd', text: 'Making a positive contribution to the world', weight: { contribution: 2, impact: 1, meaning: 1 } }
    ]
  },
  {
    id: 'stu_depth_15',
    phase: 'resilience_stress',
    type: 'situational_judgement',
    question: 'You have failed at something publicly and feel embarrassed.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would openly discuss the failure as a learning experience rather than hide it.',
    weights: {
      strongly_disagree: { private: 2, protective: 1 },
      disagree: { reserved: 1, cautious: 1 },
      agree: { open: 1, learning: 1 },
      strongly_agree: { transparent: 2, growth_mindset: 1, courageous: 1 }
    }
  },
  {
    id: 'stu_depth_16',
    phase: 'temporal_patterns',
    type: 'forced_choice',
    question: 'How do you approach planning for your future?',
    subtitle: 'Select your style',
    options: [
      { id: 'a', text: 'Detailed plans with specific milestones', weight: { structured: 2, planned: 1, detailed: 1 } },
      { id: 'b', text: 'General direction with room for flexibility', weight: { directional: 2, flexible: 1, adaptive: 1 } },
      { id: 'c', text: 'Focus on present and let future unfold', weight: { present: 2, unplanned: 1, flowing: 1 } },
      { id: 'd', text: 'Multiple scenarios depending on how things develop', weight: { contingent: 2, strategic: 1, prepared: 1 } }
    ]
  },
  {
    id: 'stu_depth_17',
    phase: 'learning_cognitive',
    type: 'scenario',
    question: 'You are assigned to work on a subject you find boring.',
    subtitle: 'How do you motivate yourself?',
    options: [
      { id: 'a', text: 'Find aspects that connect to things you do care about', weight: { connecting: 2, relevance: 1, curious: 1 } },
      { id: 'b', text: 'Focus on the outcome - grades, credits, or completion', weight: { outcome: 2, practical: 1, goal: 1 } },
      { id: 'c', text: 'Create challenges or games to make it more engaging', weight: { gamifying: 2, creative: 1, engaging: 1 } },
      { id: 'd', text: 'Push through with discipline even without interest', weight: { disciplined: 2, determined: 1, persistent: 1 } }
    ]
  },
  {
    id: 'stu_depth_18',
    phase: 'career_orientation',
    type: 'situational_judgement',
    question: 'A career advisor suggests you consider a path you had never thought about before.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would seriously explore it even if it challenges my current plans.',
    weights: {
      strongly_disagree: { fixed: 2, determined: 1 },
      disagree: { committed: 1, focused: 1 },
      agree: { open: 1, exploring: 1 },
      strongly_agree: { open: 2, exploratory: 1, flexible: 1 }
    }
  },
  {
    id: 'stu_depth_19',
    phase: 'social_personality',
    type: 'forced_choice',
    question: 'When meeting new people, you typically:',
    subtitle: 'Select your natural tendency',
    options: [
      { id: 'a', text: 'Feel energized and enjoy the interaction', weight: { extroverted: 2, energized: 1, social: 1 } },
      { id: 'b', text: 'Feel interested but need to warm up gradually', weight: { ambivert: 1, gradual: 1, warming: 1 } },
      { id: 'c', text: 'Feel drained and prefer smaller groups', weight: { introverted: 2, selective: 1, drained: 1 } },
      { id: 'd', text: 'Depends entirely on the context and people', weight: { contextual: 1, adaptive: 1, variable: 1 } }
    ]
  },
  {
    id: 'stu_depth_20',
    phase: 'values_motivation',
    type: 'reflection',
    question: 'What role does recognition from others play in your motivation?',
    subtitle: 'Select the most accurate',
    options: [
      { id: 'a', text: 'Very important - external validation motivates me', weight: { external: 2, recognition: 1, validation: 1 } },
      { id: 'b', text: 'Nice to have but not essential', weight: { balanced: 1, moderate: 1, independent: 1 } },
      { id: 'c', text: 'Less important than my own satisfaction', weight: { intrinsic: 2, self_validated: 1, internal: 1 } },
      { id: 'd', text: 'Depends on who the recognition is from', weight: { selective: 1, discerning: 1, contextual: 1 } }
    ]
  },
  {
    id: 'stu_depth_21',
    phase: 'resilience_stress',
    type: 'forced_choice',
    question: 'Your approach to dealing with uncertainty about the future is:',
    subtitle: 'Select your coping style',
    options: [
      { id: 'a', text: 'Prepare for multiple possibilities', weight: { prepared: 2, strategic: 1, planning: 1 } },
      { id: 'b', text: 'Focus on what you can control today', weight: { present: 2, grounded: 1, practical: 1 } },
      { id: 'c', text: 'Trust that things will work out somehow', weight: { optimistic: 2, trusting: 1, faith: 1 } },
      { id: 'd', text: 'Worry but push forward anyway', weight: { anxious: 1, persistent: 1, determined: 1 } }
    ]
  },
  {
    id: 'stu_depth_22',
    phase: 'temporal_patterns',
    type: 'scenario',
    question: 'Looking at people you admire, what quality do they most share?',
    subtitle: 'Select the most common trait',
    options: [
      { id: 'a', text: 'Excellence and mastery in their field', weight: { mastery: 2, excellence: 1, skill: 1 } },
      { id: 'b', text: 'Kindness and care for others', weight: { caring: 2, kind: 1, compassionate: 1 } },
      { id: 'c', text: 'Courage to be themselves', weight: { authentic: 2, courageous: 1, individual: 1 } },
      { id: 'd', text: 'Impact and contribution to the world', weight: { impact: 2, contribution: 1, meaningful: 1 } }
    ]
  },
  {
    id: 'stu_depth_23',
    phase: 'learning_cognitive',
    type: 'situational_judgement',
    question: 'You have the opportunity to skip foundation material and jump to advanced content.',
    subtitle: 'Rate your agreement:',
    scale: ['Strongly Disagree', 'Disagree', 'Agree', 'Strongly Agree'],
    statement: 'I would still cover the fundamentals thoroughly first.',
    weights: {
      strongly_disagree: { impatient: 2, advanced: 1 },
      disagree: { flexible: 1, pragmatic: 1 },
      agree: { thorough: 1, foundational: 1 },
      strongly_agree: { thorough: 2, methodical: 1, disciplined: 1 }
    }
  },
  {
    id: 'stu_depth_24',
    phase: 'career_orientation',
    type: 'forced_choice',
    question: 'Which combination of rewards would be most motivating in a job?',
    subtitle: 'Select the most appealing package',
    options: [
      { id: 'a', text: 'High salary with standard work', weight: { financial: 2, compensation: 1, material: 1 } },
      { id: 'b', text: 'Moderate salary with meaningful impact', weight: { purpose: 2, meaning: 1, balanced: 1 } },
      { id: 'c', text: 'Variable income with ownership opportunity', weight: { entrepreneurial: 2, ownership: 1, upside: 1 } },
      { id: 'd', text: 'Good salary with excellent work-life balance', weight: { balance: 2, lifestyle: 1, wellbeing: 1 } }
    ]
  },
  {
    id: 'stu_depth_25',
    phase: 'social_personality',
    type: 'reflection',
    question: 'How comfortable are you asking for help when you need it?',
    subtitle: 'Select the most accurate',
    options: [
      { id: 'a', text: 'Very comfortable - everyone needs help sometimes', weight: { help_seeking: 2, comfortable: 1, collaborative: 1 } },
      { id: 'b', text: 'Comfortable with people I trust', weight: { selective: 1, trusting: 1, boundaried: 1 } },
      { id: 'c', text: 'Prefer to figure things out myself first', weight: { independent: 2, self_reliant: 1, autonomous: 1 } },
      { id: 'd', text: 'Find it difficult - I do not want to be a burden', weight: { reluctant: 1, considerate: 1, independent: 1 } }
    ]
  }
];

// Combine all questions
export const STUDENT_QUESTIONS_V2_FULL = [...STUDENT_QUESTIONS_V2, ...STUDENT_DEPTH_QUESTIONS];

// Weight mapping to Student Report Dimensions
export const STUDENT_DIMENSION_MAPPING = {
  learning_style: ['theoretical', 'experiential', 'visual', 'verbal', 'social_learning', 'independent'],
  cognitive_approach: ['analytical', 'creative', 'systematic', 'intuitive', 'detail', 'conceptual'],
  career_orientation: ['creating', 'investigative', 'social', 'enterprising', 'organizing', 'implementing'],
  work_values: ['achievement', 'service', 'creativity', 'security', 'autonomy', 'recognition'],
  social_orientation: ['extroverted', 'introverted', 'collaborative', 'independent', 'leadership', 'supportive'],
  motivation_drivers: ['intrinsic', 'extrinsic', 'purpose', 'mastery', 'connection', 'impact'],
  resilience_pattern: ['optimistic', 'resilient', 'adaptable', 'sensitive', 'persistent', 'composed'],
  decision_style: ['logical', 'intuitive', 'collaborative', 'principled', 'pragmatic']
};

// Question count configuration for students
export const STUDENT_CONFIG = {
  totalQuestions: 85,
  timeMinutes: 30,
  phases: {
    learning_cognitive: { min: 15, max: 20 },
    career_orientation: { min: 15, max: 20 },
    social_personality: { min: 15, max: 20 },
    values_motivation: { min: 12, max: 15 },
    resilience_stress: { min: 12, max: 15 },
    temporal_patterns: { min: 8, max: 12 }
  }
};
